import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Users, LogOut, TrendingUp, DollarSign, Clock, UserPlus, BarChart3, Eye, Sun, Moon, ChevronLeft, ChevronRight, Check, X, ExternalLink, FileText, Video, Search, QrCode, Play, CheckCircle, Loader2, AlertCircle, Building2, Filter, Bell, BellOff, Volume2, VolumeX, MessageSquare, BookOpen, RefreshCcw, ArrowUp, ArrowDown, Menu, Home, Star, CalendarClock, UserCircle, Shield, Edit3, MapPin, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import InternalMessages from '../components/InternalMessages';
import NotificationBell from '../components/NotificationBell';
import MessageBell from '../components/MessageBell';
import Breadcrumb from '../components/Breadcrumb';
import ClinicalTemplates from '../components/ClinicalTemplates';
import ReviewForm from '../components/ReviewForm';
import { ShiftsManager } from '../components/ShiftsManager';
import VideoConsultManager from '../components/VideoConsultManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

// Legenda colori sala d'attesa
const STATUS_CONFIG = {
  scheduled: { color: '#F59E0B', bg: '#FFFBEB', label: 'In Attesa', emoji: '🟡' },
  checked_in: { color: '#10B981', bg: '#ECFDF5', label: 'Arrivato', emoji: '🟢' },
  paid: { color: '#EF4444', bg: '#FEF2F2', label: 'Pronto per Visita', emoji: '🔴' },
  in_progress: { color: '#8B5CF6', bg: '#F5F3FF', label: 'In Visita', emoji: '🟣' },
  completed: { color: '#2A9FCB', bg: 'linear-gradient(145deg, #e8f4fd 0%, #f4f9fe 40%, #dcedf8 100%)', label: 'Completato', emoji: '🔵' },
  cancelled: { color: '#6B7280', bg: '#F9FAFB', label: 'Cancellato', emoji: '⚫' }
};

// Colori per le stanze
const ROOM_COLORS = {
  'Stanza A': { bg: '#DBEAFE', text: '#2A9FCB', border: '#93C5FD' },
  'Stanza B': { bg: '#FEF3C7', text: '#B45309', border: '#FCD34D' },
  'Stanza C': { bg: '#D1FAE5', text: '#047857', border: '#6EE7B7' },
  'Stanza D': { bg: '#EDE9FE', text: '#6D28D9', border: '#C4B5FD' },
};

export const DoctorDashboard = () => {
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('agenda');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [availability, setAvailability] = useState({});
  const [bookingsCount, setBookingsCount] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [availabilityLocation, setAvailabilityLocation] = useState('milano');
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [loadingPatients, setLoadingPatients] = useState(false);
  
  // Register Patient Modal
  const [showRegisterPatientModal, setShowRegisterPatientModal] = useState(false);
  const [registerPatientData, setRegisterPatientData] = useState({
    first_name: '', last_name: '', email: '', phone: '', date_of_birth: '', gender: '', fiscal_code: ''
  });
  const [registerPatientLoading, setRegisterPatientLoading] = useState(false);
  
  // Read tab from URL params (for deep-linking from notifications)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['agenda', 'stats', 'today', 'pazienti', 'sala-attesa', 'turni', 'videoconsulti', 'profilo', 'messages', 'templates'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);
  
  // Day View state (Google Calendar style)
  const [showDayView, setShowDayView] = useState(false);
  const [dayViewDate, setDayViewDate] = useState(null);
  const [dayAppointments, setDayAppointments] = useState([]);
  const [dayViewData, setDayViewData] = useState(null);
  const [loadingDayView, setLoadingDayView] = useState(false);
  
  // Chart stats state
  const [chartData, setChartData] = useState(null);
  const [chartPeriod, setChartPeriod] = useState('month');
  const [loadingChart, setLoadingChart] = useState(false);
  const [companyChartData, setCompanyChartData] = useState(null);
  const [showCompanyStats, setShowCompanyStats] = useState(false);
  
  // Sala d'attesa state
  const [salaAttesaDate, setSalaAttesaDate] = useState(new Date().toISOString().split('T')[0]);
  const [salaAttesaData, setSalaAttesaData] = useState(null);
  const [loadingSalaAttesa, setLoadingSalaAttesa] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  
  // Multi-doctor/room filters
  const [filterDoctor, setFilterDoctor] = useState('');
  const [filterRoom, setFilterRoom] = useState('');
  const [doctorsToday, setDoctorsToday] = useState([]);
  const [roomsToday, setRoomsToday] = useState([]);
  const [viewMode, setViewMode] = useState('status'); // 'status' or 'room'
  
  // Alert system state
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [newArrivals, setNewArrivals] = useState([]);
  const [previousPatientCount, setPreviousPatientCount] = useState(0);
  const [showAlertBanner, setShowAlertBanner] = useState(false);
  
  // Patient filters state
  const [patientFilters, setPatientFilters] = useState({
    gender: '',
    ethnicity: '',
    ageMin: '',
    ageMax: ''
  });
  const [showPatientFilters, setShowPatientFilters] = useState(false);
  
  // Availability confirmation modal state
  const [availabilityModal, setAvailabilityModal] = useState({
    show: false,
    date: null,
    period: null,
    currentValue: false,
    newValue: false,
    affectedAppointments: []
  });
  
  // Internal messages state
  const [showMessages, setShowMessages] = useState(false);
  
  // Google Calendar sync modal state
  const [calendarSyncModal, setCalendarSyncModal] = useState({
    show: false,
    loading: false,
    syncData: null
  });
  
  // Patient profile modal state
  const [patientProfileModal, setPatientProfileModal] = useState({
    show: false,
    patient: null,
    loading: false,
    formData: {}
  });
  
  // Doctor profile edit state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    numero_ordine: '',
    specialization: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  const navigate = useNavigate();

  // Filtered patients based on search and filters
  const filteredPatients = useMemo(() => {
    let result = patients;
    
    // Search filter
    if (patientSearch) {
      const search = patientSearch.toLowerCase();
      result = result.filter(p => 
        p.first_name?.toLowerCase().includes(search) ||
        p.last_name?.toLowerCase().includes(search) ||
        p.email?.toLowerCase().includes(search) ||
        p.phone?.includes(search) ||
        p.codice_fiscale?.toLowerCase().includes(search)
      );
    }
    
    // Gender filter
    if (patientFilters.gender) {
      result = result.filter(p => p.gender === patientFilters.gender);
    }
    
    // Ethnicity filter
    if (patientFilters.ethnicity) {
      result = result.filter(p => p.ethnicity === patientFilters.ethnicity);
    }
    
    // Age range filter
    if (patientFilters.ageMin || patientFilters.ageMax) {
      const currentYear = new Date().getFullYear();
      result = result.filter(p => {
        if (!p.date_of_birth) return false;
        const birthYear = new Date(p.date_of_birth).getFullYear();
        const age = currentYear - birthYear;
        
        if (patientFilters.ageMin && age < parseInt(patientFilters.ageMin)) return false;
        if (patientFilters.ageMax && age > parseInt(patientFilters.ageMax)) return false;
        return true;
      });
    }
    
    // Birth year filters (legacy)
    if (patientFilters.birthYearFrom) {
      result = result.filter(p => {
        if (!p.date_of_birth) return false;
        const year = new Date(p.date_of_birth).getFullYear();
        return year >= parseInt(patientFilters.birthYearFrom);
      });
    }
    if (patientFilters.birthYearTo) {
      result = result.filter(p => {
        if (!p.date_of_birth) return false;
        const year = new Date(p.date_of_birth).getFullYear();
        return year <= parseInt(patientFilters.birthYearTo);
      });
    }
    
    // Last visit filters
    if (patientFilters.lastVisitFrom) {
      result = result.filter(p => {
        if (!p.last_visit_date) return false;
        return p.last_visit_date >= patientFilters.lastVisitFrom;
      });
    }
    if (patientFilters.lastVisitTo) {
      result = result.filter(p => {
        if (!p.last_visit_date) return false;
        return p.last_visit_date <= patientFilters.lastVisitTo;
      });
    }
    
    // Diagnosis tag filter
    if (patientFilters.diagnosisTag) {
      const tagSearch = patientFilters.diagnosisTag.toLowerCase();
      result = result.filter(p => {
        const tags = p.diagnosis_tags || [];
        return tags.some(tag => tag.toLowerCase().includes(tagSearch));
      });
    }
    
    return result;
  }, [patients, patientSearch, patientFilters]);

  // Play alert sound
  const playAlertSound = () => {
    if (!soundEnabled) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5 note
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      
      // Second beep
      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();
        osc2.connect(gain2);
        gain2.connect(audioContext.destination);
        osc2.frequency.setValueAtTime(1046.5, audioContext.currentTime); // C6 note
        osc2.type = 'sine';
        gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        osc2.start(audioContext.currentTime);
        osc2.stop(audioContext.currentTime + 0.3);
      }, 200);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  // Register new patient
  const handleRegisterPatient = async () => {
    const { first_name, last_name, email, phone, date_of_birth } = registerPatientData;
    if (!first_name || !last_name || !email || !phone || !date_of_birth) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    try {
      setRegisterPatientLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/patients/create`, registerPatientData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Paziente ${first_name} ${last_name} registrato con successo!`);
      setShowRegisterPatientModal(false);
      setRegisterPatientData({ first_name: '', last_name: '', email: '', phone: '', date_of_birth: '', gender: '', fiscal_code: '' });
      // Reload patients
      fetchPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella registrazione');
    } finally {
      setRegisterPatientLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      setLoadingPatients(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/patients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatients(res.data);
    } catch (err) {
      console.error('Error loading patients:', err);
    } finally {
      setLoadingPatients(false);
    }
  };

  // Check for new arrivals
  useEffect(() => {
    if (!alertsEnabled || !salaAttesaData?.patients) return;
    
    const currentReadyCount = salaAttesaData.patients.filter(p => 
      p.waiting_status === 'in_attesa' || p.appointment_status === 'checked_in' || p.appointment_status === 'paid'
    ).length;
    
    if (currentReadyCount > previousPatientCount && previousPatientCount > 0) {
      // New patient arrived!
      playAlertSound();
      setShowAlertBanner(true);
      toast.success('🔔 Nuovo paziente in sala d\'attesa!', {
        duration: 5000,
        icon: '👤'
      });
      
      // Auto-hide banner after 10 seconds
      setTimeout(() => setShowAlertBanner(false), 10000);
    }
    
    setPreviousPatientCount(currentReadyCount);
  }, [salaAttesaData, alertsEnabled, soundEnabled]);

  // Auto-refresh sala d'attesa every 30 seconds
  useEffect(() => {
    if (activeTab !== 'sala-attesa') return;
    
    const interval = setInterval(() => {
      loadSalaAttesa(salaAttesaDate, filterDoctor, filterRoom);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [activeTab, salaAttesaDate, filterDoctor, filterRoom]);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/auth');
      return;
    }
    const parsedUser = JSON.parse(userData);
    // Permetti accesso a medici e ortottisti
    if (parsedUser.role !== 'doctor' && parsedUser.role !== 'ortottista') {
      navigate('/');
      return;
    }
    setUser(parsedUser);
    loadData();
    loadPatients();
    
    // Auto-refresh appuntamenti ogni 30 secondi per sincronizzazione real-time
    const refreshInterval = setInterval(() => {
      loadData();
    }, 30000);
    
    return () => clearInterval(refreshInterval);
  }, [navigate]);

  useEffect(() => {
    if (user) {
      loadAvailability();
      // Initialize profile form with user data
      setProfileForm({
        numero_ordine: user.numero_ordine || '',
        specialization: user.specialization || ''
      });
    }
  }, [currentMonth, currentYear, user, availabilityLocation]);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [apptRes, statsRes] = await Promise.all([
        axios.get(`${API}/appointments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/stats/doctor`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      // Trasforma gli appuntamenti per compatibilità con il frontend
      const rawAppointments = apptRes.data || [];
      const transformedAppointments = rawAppointments.map(apt => ({
        ...apt,
        date: apt.appointment_date ? apt.appointment_date.split('T')[0] : apt.date,
        time: apt.appointment_date ? apt.appointment_date.split('T')[1]?.substring(0, 5) : apt.time
      }));
      setAppointments(transformedAppointments);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadPatients = async () => {
    const token = localStorage.getItem('token');
    setLoadingPatients(true);
    try {
      const res = await axios.get(`${API}/admin/patients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatients(res.data);
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setLoadingPatients(false);
    }
  };

  // Open patient profile modal
  const openPatientProfile = (patient, e) => {
    if (e) e.stopPropagation();
    setPatientProfileModal({
      show: true,
      patient: patient,
      loading: false,
      formData: {
        first_name: patient.first_name || '',
        last_name: patient.last_name || '',
        phone: patient.phone || '',
        email: patient.email || '',
        date_of_birth: patient.date_of_birth || '',
        place_of_birth: patient.place_of_birth || '',
        gender: patient.gender || '',
        ethnicity: patient.ethnicity || '',
        address_street: patient.address_street || '',
        address_number: patient.address_number || '',
        address_cap: patient.address_cap || '',
        address_city: patient.address_city || '',
        codice_fiscale: patient.codice_fiscale || '',
        blood_type: patient.blood_type || ''
      }
    });
  };

  // Update patient profile form field
  const updatePatientFormField = (field, value) => {
    setPatientProfileModal(prev => ({
      ...prev,
      formData: { ...prev.formData, [field]: value }
    }));
  };

  // Save patient profile
  const savePatientProfile = async () => {
    const token = localStorage.getItem('token');
    setPatientProfileModal(prev => ({ ...prev, loading: true }));
    
    try {
      const res = await axios.put(
        `${API}/admin/patients/${patientProfileModal.patient.id}/profile`,
        {
          first_name: patientProfileModal.formData.first_name,
          last_name: patientProfileModal.formData.last_name,
          phone: patientProfileModal.formData.phone,
          date_of_birth: patientProfileModal.formData.date_of_birth,
          place_of_birth: patientProfileModal.formData.place_of_birth,
          gender: patientProfileModal.formData.gender,
          ethnicity: patientProfileModal.formData.ethnicity,
          address_street: patientProfileModal.formData.address_street,
          address_number: patientProfileModal.formData.address_number,
          address_zip: patientProfileModal.formData.address_cap,
          address_city: patientProfileModal.formData.address_city,
          codice_fiscale: patientProfileModal.formData.codice_fiscale,
          blood_type: patientProfileModal.formData.blood_type
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Profilo paziente aggiornato!');
      
      // Update local patients list
      setPatients(prev => prev.map(p => 
        p.id === patientProfileModal.patient.id ? res.data.patient : p
      ));
      
      setPatientProfileModal({ show: false, patient: null, loading: false, formData: {} });
    } catch (error) {
      console.error('Error saving patient profile:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setPatientProfileModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Save doctor profile (numero_ordine, specialization)
  const saveDoctorProfile = async () => {
    const token = localStorage.getItem('token');
    setSavingProfile(true);
    
    try {
      const res = await axios.put(
        `${API}/doctors/me/profile`,
        {
          numero_ordine: profileForm.numero_ordine,
          specialization: profileForm.specialization
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Profilo aggiornato con successo!');
      
      // Update local user state
      const updatedUser = { ...user, ...res.data.user };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setEditingProfile(false);
    } catch (error) {
      console.error('Error saving doctor profile:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSavingProfile(false);
    }
  };

  // Load chart data for statistics
  const loadChartData = async (period = 'month') => {
    const token = localStorage.getItem('token');
    setLoadingChart(true);
    try {
      // Carica statistiche personali
      const res = await axios.get(`${API}/stats/staff/${user?.id}/chart?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChartData(res.data);
      
      // Carica anche statistiche azienda
      const companyRes = await axios.get(`${API}/stats/company/chart?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyChartData(companyRes.data);
    } catch (error) {
      console.error('Error loading chart data:', error);
    } finally {
      setLoadingChart(false);
    }
  };

  // Load chart when stats tab is active or period changes
  useEffect(() => {
    if (activeTab === 'stats' && user) {
      loadChartData(chartPeriod);
    }
  }, [activeTab, chartPeriod, user]);

  const loadAvailability = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/doctor/availability?month=${currentMonth}&year=${currentYear}&location=${availabilityLocation}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert array to dict
      const availDict = {};
      res.data.availabilities.forEach(a => {
        availDict[a.date] = a;
      });
      setAvailability(availDict);
      setBookingsCount(res.data.bookings_count || {});
    } catch (error) {
      console.error('Error loading availability:', error);
    }
  };

  // Load day appointments for Google Calendar style view
  const loadDayAppointments = async (dateStr) => {
    const token = localStorage.getItem('token');
    setLoadingDayView(true);
    try {
      const res = await axios.get(`${API}/doctor/day-appointments/${dateStr}?location=${availabilityLocation}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDayViewData(res.data);
      setDayAppointments(res.data.appointments || []);
      setDayViewDate(dateStr);
      setShowDayView(true);
    } catch (error) {
      console.error('Error loading day appointments:', error);
      toast.error('Errore nel caricamento degli appuntamenti');
    } finally {
      setLoadingDayView(false);
    }
  };

  // Open day view when clicking on a day number
  const openDayView = (dateStr, hasAvailability) => {
    if (hasAvailability) {
      loadDayAppointments(dateStr);
    } else {
      toast.info('Nessuna disponibilità impostata per questo giorno');
    }
  };

  // Sala d'Attesa functions
  const loadSalaAttesa = async (date, doctorFilter = '', roomFilter = '') => {
    const token = localStorage.getItem('token');
    setLoadingSalaAttesa(true);
    try {
      // Build query params
      let url = `${API}/sala-attesa/${date}`;
      const params = [];
      if (doctorFilter) params.push(`doctor_id=${doctorFilter}`);
      if (roomFilter) params.push(`room=${encodeURIComponent(roomFilter)}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const [salaRes, qrRes] = await Promise.all([
        axios.get(url, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/sala-attesa/qr-code/${date}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSalaAttesaData(salaRes.data);
      setQrCodeUrl(qrRes.data.qr_code_url);
      setDoctorsToday(salaRes.data.doctors_today || []);
      setRoomsToday(salaRes.data.rooms_today || []);
    } catch (error) {
      console.error('Error loading sala attesa:', error);
      toast.error('Errore nel caricamento della sala d\'attesa');
    } finally {
      setLoadingSalaAttesa(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'sala-attesa' && salaAttesaDate) {
      loadSalaAttesa(salaAttesaDate, filterDoctor, filterRoom);
    }
  }, [activeTab, salaAttesaDate, filterDoctor, filterRoom]);

  const changeSalaAttesaDate = (days) => {
    const current = new Date(salaAttesaDate);
    current.setDate(current.getDate() + days);
    setSalaAttesaDate(current.toISOString().split('T')[0]);
  };

  // Call patient to specific room
  const callPatientToRoom = async (appointmentId, room, patientId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/sala-attesa/call-patient`, {
        appointment_id: appointmentId,
        room: room
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const notifications = [];
      if (res.data.sms_sent) notifications.push('SMS');
      if (res.data.email_sent) notifications.push('Email');
      
      if (notifications.length > 0) {
        toast.success(`📢 ${res.data.patient_name} chiamato in Stanza ${room} - ${notifications.join(' e ')} inviati!`);
      } else {
        toast.success(`📢 ${res.data.patient_name} chiamato in Stanza ${room} - Notifica in-app inviata`);
      }
      
      // Reload sala attesa to update status
      loadSalaAttesa(salaAttesaDate, filterDoctor, filterRoom);
      
      // Apri la CARTELLA CLINICA del paziente (unificata)
      navigate(`/cartella-clinica/${patientId}`);
      
    } catch (error) {
      console.error('Error calling patient:', error);
      toast.error(error.response?.data?.detail || 'Errore nella chiamata paziente');
    }
  };

  // Reset filters
  const resetFilters = () => {
    setFilterDoctor('');
    setFilterRoom('');
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    const token = localStorage.getItem('token');
    try {
      let endpoint = '';
      switch (newStatus) {
        case 'paid':
          endpoint = `/sala-attesa/confirm-payment/${appointmentId}`;
          break;
        case 'in_progress':
          endpoint = `/sala-attesa/start-visit/${appointmentId}`;
          break;
        case 'completed':
          endpoint = `/sala-attesa/complete-visit/${appointmentId}`;
          break;
        default:
          endpoint = `/sala-attesa/update-status`;
      }
      
      if (newStatus === 'paid' || newStatus === 'in_progress' || newStatus === 'completed') {
        await axios.post(`${API}${endpoint}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.put(`${API}${endpoint}`, { appointment_id: appointmentId, new_status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      }
      
      toast.success(`Stato aggiornato: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      loadSalaAttesa(salaAttesaDate, filterDoctor, filterRoom);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const toggleAvailability = (date, period) => {
    console.log('toggleAvailability called:', date, period);
    const current = availability[date] || { morning_available: false, afternoon_available: false };
    const currentValue = period === 'morning' ? current.morning_available : current.afternoon_available;
    const newValue = !currentValue;
    
    console.log('Current value:', currentValue, 'New value:', newValue);
    
    // Check if there are affected appointments when deselecting
    const periodLabel = period === 'morning' ? 'mattina' : 'pomeriggio';
    let affectedAppointments = [];
    
    if (currentValue && !newValue) {
      // Deselecting - check for existing appointments
      affectedAppointments = appointments.filter(apt => {
        const aptDate = apt.appointment_date.split('T')[0];
        const aptHour = new Date(apt.appointment_date).getHours();
        const isPeriodMatch = period === 'morning' ? aptHour < 13 : aptHour >= 13;
        return aptDate === date && isPeriodMatch && apt.status !== 'cancelled' && apt.status !== 'completed';
      });
    }
    
    console.log('Setting modal with:', { show: true, date, period, periodLabel, currentValue, newValue, affectedAppointments });
    
    // Show confirmation modal
    setAvailabilityModal({
      show: true,
      date,
      period,
      periodLabel,
      currentValue,
      newValue,
      affectedAppointments
    });
    
    console.log('Modal state should be updated');
  };
  
  const confirmAvailabilityChange = async () => {
    const { date, period, newValue, affectedAppointments } = availabilityModal;
    const token = localStorage.getItem('token');
    const current = availability[date] || { morning_available: false, afternoon_available: false };
    
    const newAvail = {
      date,
      morning_available: period === 'morning' ? newValue : current.morning_available,
      afternoon_available: period === 'afternoon' ? newValue : current.afternoon_available,
      location: availabilityLocation
    };
    
    setSaving(true);
    try {
      // If deselecting with affected appointments, notify them
      if (!newValue && affectedAppointments.length > 0) {
        await axios.post(`${API}/doctor/availability/cancel-slot`, {
          date,
          period,
          notify_patients: true,
          affected_appointment_ids: affectedAppointments.map(a => a.id)
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Slot disattivato. ${affectedAppointments.length} paziente/i notificati`);
      } else {
        await axios.post(`${API}/doctor/availability`, newAvail, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (newValue) {
          toast.success('Slot attivato! Admin notificato della nuova disponibilità');
        } else {
          toast.success('Slot disattivato');
        }
      }
      
      setAvailability(prev => ({
        ...prev,
        [date]: newAvail
      }));
    } catch (error) {
      console.error('Error setting availability:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
      setAvailabilityModal({ show: false, date: null, period: null, currentValue: false, newValue: false, affectedAppointments: [] });
    }
  };

  const setFullMonth = async (period, value) => {
    const token = localStorage.getItem('token');
    const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
    const today = new Date();
    
    const availabilities = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth - 1, day);
      const dateStr = date.toISOString().split('T')[0];
      
      // Skip past dates and Sundays
      if (date < today || date.getDay() === 0) continue;
      
      const current = availability[dateStr] || { morning_available: false, afternoon_available: false };
      
      availabilities.push({
        date: dateStr,
        morning_available: period === 'morning' ? value : current.morning_available,
        afternoon_available: period === 'afternoon' ? value : current.afternoon_available,
        location: availabilityLocation
      });
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/doctor/availability/bulk`, availabilities, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadAvailability();
    } catch (error) {
      console.error('Error setting bulk availability:', error);
    } finally {
      setSaving(false);
    }
  };

  const prevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const syncGoogleCalendar = async () => {
    const token = localStorage.getItem('token');
    setCalendarSyncModal({ show: true, loading: true, syncData: null });
    
    try {
      const res = await axios.get(`${API}/calendar/sync/setup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarSyncModal({ show: true, loading: false, syncData: res.data });
    } catch (error) {
      console.error('Error setting up calendar sync:', error);
      toast.error('Errore nella configurazione della sincronizzazione');
      setCalendarSyncModal({ show: false, loading: false, syncData: null });
    }
  };

  const openGoogleCalendarSync = () => {
    if (calendarSyncModal.syncData?.google_calendar_url) {
      window.open(calendarSyncModal.syncData.google_calendar_url, '_blank');
      toast.success('🗓️ Reindirizzamento a Google Calendar...');
    }
  };

  const copyFeedUrl = () => {
    if (calendarSyncModal.syncData?.feed_url) {
      navigator.clipboard.writeText(calendarSyncModal.syncData.feed_url);
      toast.success('📋 URL copiato negli appunti!');
    }
  };

  // Calendar helpers
  const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1).getDay();
  const offset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
  const today = new Date();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const todayStr = today.toISOString().split('T')[0];
  const todayAppointments = appointments.filter(a => {
    const apptDate = new Date(a.appointment_date).toISOString().split('T')[0];
    return apptDate === todayStr && a.status === 'scheduled';
  }).sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{background: '#F9FAFB'}}>
      {/* Mobile Header with Hamburger */}
      <div className="lg:hidden flex items-center justify-between p-4 bg-gradient-to-br from-[#e8f4fd] via-[#f4f9fe] to-[#dcedf8] border-b border-[#2A9FCB]/20">
        <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <img src="/logo-primevision-small.png" alt="PRIME VISION" className="w-10 h-10 object-contain" />
          <p className="text-xs mt-1" style={{color: '#6B7280'}}>Area Medico</p>
        </Link>
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-white/50 transition-all"
          style={{color: '#2A9FCB'}}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setMobileMenuOpen(false)}>
          <div 
            className="absolute top-0 right-0 w-72 h-full bg-gradient-to-br from-[#e8f4fd] via-[#f4f9fe] to-[#dcedf8] p-6 overflow-y-auto shadow-xl animate-slide-in-right"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                <img src="/logo-primevision-small.png" alt="PRIME VISION" className="w-8 h-8 object-contain" />
                <p className="text-xs mt-1" style={{color: '#6B7280'}}>Area Medico</p>
              </Link>
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-lg hover:bg-white/50" style={{color: '#2A9FCB'}}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="space-y-2">
              {[
                { id: 'agenda', icon: Calendar, label: 'Agenda' },
                { id: 'stats', icon: BarChart3, label: 'Statistiche' },
                { id: 'today', icon: Clock, label: `Oggi (${todayAppointments.length})` },
                { id: 'pazienti', icon: Users, label: `Pazienti (${patients.length})` },
                { id: 'sala-attesa', icon: QrCode, label: "Sala d'Attesa" },
                { id: 'turni', icon: CalendarClock, label: 'Turni e Disponibilità' },
                { id: 'videoconsulti', icon: Video, label: 'Videoconsulti' },
                { id: 'templates', icon: FileText, label: 'Template Clinici' },
                { id: 'profilo', icon: UserCircle, label: 'Profilo e Sicurezza' },
                { id: 'reviews', icon: Star, label: 'Recensioni' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${activeTab === tab.id ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
                  style={{color: '#2A9FCB'}}
                >
                  <tab.icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>

            <button onClick={() => { setShowMessages(true); setMobileMenuOpen(false); }} className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg mt-4 hover:bg-green-50 border-2 border-transparent hover:border-green-200" style={{color: '#2A9FCB'}}>
              <MessageSquare className="w-5 h-5" /><span>Messaggi</span>
            </button>

            <div className="border-t border-[#2A9FCB]/20 mt-4 pt-4 space-y-1">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-white/50 text-sm" style={{color: '#6B7280'}}>
                <ExternalLink className="w-4 h-4" /><span>Home</span>
              </button>
              <button onClick={() => { navigate('/linee-guida'); setMobileMenuOpen(false); }} className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-white/50 text-sm" style={{color: '#6B7280'}}>
                <FileText className="w-4 h-4" /><span>Linee Guida</span>
              </button>
            </div>

            <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg mt-4 hover:bg-red-50" style={{color: '#EF4444'}}>
              <LogOut className="w-5 h-5" /><span>Esci</span>
            </button>
          </div>
        </div>
      )}

      {/* Desktop Sidebar - Hidden on mobile */}
      <div className="hidden lg:block w-64 p-6 flex-shrink-0" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
        <Link to="/" className="flex items-center gap-3 mb-8 hover:opacity-80 transition-opacity">
          <img src="/logo-primevision-small.png" alt="PRIME VISION" className="w-12 h-12 object-contain" />
          <p className="text-sm mt-1" style={{color: '#6B7280'}}>Area Medico</p>
        </Link>

        <nav className="space-y-2">
          <button
            onClick={() => setActiveTab('agenda')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${activeTab === 'agenda' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
            style={{color: '#2A9FCB'}}
          >
            <Calendar className="w-5 h-5" />
            <span>Agenda</span>
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${activeTab === 'stats' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
            style={{color: '#2A9FCB'}}
          >
            <BarChart3 className="w-5 h-5" />
            <span>Statistiche</span>
          </button>
          <button
            onClick={() => setActiveTab('today')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${activeTab === 'today' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
            style={{color: '#2A9FCB'}}
          >
            <Clock className="w-5 h-5" />
            <span>Oggi ({todayAppointments.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('pazienti')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${activeTab === 'pazienti' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
            style={{color: '#2A9FCB'}}
          >
            <Users className="w-5 h-5" />
            <span>Pazienti ({patients.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('sala-attesa')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${activeTab === 'sala-attesa' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
            style={{color: '#2A9FCB'}}
          >
            <QrCode className="w-5 h-5" />
            <span>Sala d&apos;Attesa</span>
          </button>
          <button
            onClick={() => setActiveTab('turni')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${activeTab === 'turni' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
            style={{color: '#2A9FCB'}}
          >
            <CalendarClock className="w-5 h-5" />
            <span>Turni e Disponibilità</span>
          </button>
          <button
            onClick={() => setActiveTab('videoconsulti')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${activeTab === 'videoconsulti' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
            style={{color: '#2A9FCB'}}
          >
            <Video className="w-5 h-5" />
            <span>Videoconsulti</span>
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${activeTab === 'templates' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
            style={{color: '#2A9FCB'}}
            data-testid="tab-templates"
          >
            <FileText className="w-5 h-5" />
            <span>Template Clinici</span>
          </button>
          <button
            onClick={() => setActiveTab('profilo')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${activeTab === 'profilo' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
            style={{color: '#2A9FCB'}}
          >
            <UserCircle className="w-5 h-5" />
            <span>Profilo e Sicurezza</span>
          </button>
        </nav>

        <div className="mt-8 p-4 rounded-xl" style={{background: 'rgba(127, 179, 160, 0.2)'}}>
          <button
            onClick={syncGoogleCalendar}
            className="w-full flex items-center justify-center gap-2 text-sm font-medium py-2 rounded-lg bg-white shadow-sm"
            style={{color: '#2A9FCB'}}
          >
            <ExternalLink className="w-4 h-4" />
            Sync Google Calendar
          </button>
        </div>

        {/* Messages Button */}
        <button 
          onClick={() => setShowMessages(true)} 
          className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg mt-4 hover:bg-green-50 transition-all border-2 border-transparent hover:border-green-200"
          style={{color: '#2A9FCB'}}
          data-testid="open-messages-btn"
        >
          <MessageSquare className="w-5 h-5" />
          <span>Messaggi</span>
        </button>

        {/* Link Navigazione Sito */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-[#2A9FCB]/60 mb-3 px-2">NAVIGAZIONE SITO</p>
          <button 
            onClick={() => navigate('/')} 
            className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-white/50 transition-all text-sm"
            style={{color: '#6B7280'}}
          >
            <ExternalLink className="w-4 h-4" />
            <span>Home</span>
          </button>
          <button 
            onClick={() => navigate('/tests')} 
            className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-white/50 transition-all text-sm"
            style={{color: '#6B7280'}}
          >
            <Eye className="w-4 h-4" />
            <span>Test Visivi</span>
          </button>
          <button 
            onClick={() => navigate('/linee-guida')} 
            className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-white/50 transition-all text-sm"
            style={{color: '#6B7280'}}
          >
            <FileText className="w-4 h-4" />
            <span>Linee Guida</span>
          </button>
          <button 
            onClick={() => navigate('/manualistica')} 
            className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-amber-50 transition-all text-sm font-medium"
            style={{color: '#B45309'}}
          >
            <BookOpen className="w-4 h-4" />
            <span>Manualistica</span>
          </button>
        </div>

        <button onClick={handleLogout} className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg mt-4 hover:bg-red-50 transition-all" style={{color: '#EF4444'}}>
          <LogOut className="w-5 h-5" />
          <span>Esci</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb Navigation */}
          <Breadcrumb 
            items={[
              { label: 'Home', path: '/' },
              { label: 'Dashboard Medico', path: '/doctor' }
            ]}
            currentPage={
              activeTab === 'agenda' ? 'Agenda' :
              activeTab === 'stats' ? 'Statistiche' :
              activeTab === 'today' ? 'Appuntamenti Oggi' :
              activeTab === 'pazienti' ? 'Pazienti' :
              activeTab === 'sala-attesa' ? "Sala d'Attesa" :
              activeTab === 'turni' ? 'Turni e Disponibilità' :
              activeTab === 'videoconsulti' ? 'Videoconsulti' :
              activeTab === 'profilo' ? 'Profilo e Sicurezza' :
              activeTab === 'messages' ? 'Messaggi' :
              activeTab === 'templates' ? 'Template Clinici' : null
            }
          />
          
          {/* Header with notifications */}
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl lg:text-3xl font-bold" style={{color: '#2c4667'}}>
              Benvenuto, Dott. {user?.last_name}
            </h1>
            <div className="flex items-center gap-2 lg:gap-3">
              <NotificationBell userId={user?.id} />
              <MessageBell userId={user?.id} iconColor="#2c4667" currentUser={user} />
            </div>
          </div>
          <p className="text-[#2c4667]/70 mb-6 text-sm lg:text-base">Gestisci la tua disponibilità e visualizza gli appuntamenti</p>

          {/* Agenda Tab - Calendar */}
          {activeTab === 'agenda' && (
            <div className="space-y-6">
              {/* Location Toggle */}
              <div className="flex items-center gap-3 p-4 rounded-xl border-2 transition-all" 
                data-testid="doctor-location-toggle"
                style={{
                  background: availabilityLocation === 'milano' ? '#FFF7ED' : '#EFF6FF',
                  borderColor: availabilityLocation === 'milano' ? '#F97316' : '#2A9FCB'
                }}
              >
                <MapPin className="w-5 h-5" style={{ color: availabilityLocation === 'milano' ? '#F97316' : '#2A9FCB' }} />
                <span className="text-sm font-semibold" style={{ color: availabilityLocation === 'milano' ? '#9A3412' : '#1E40AF' }}>
                  Stai visualizzando la sede di:
                </span>
                <button
                  onClick={() => setAvailabilityLocation('milano')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${availabilityLocation === 'milano' ? 'bg-orange-500 text-white shadow-md scale-105' : 'text-orange-600 hover:bg-orange-50 border border-orange-200'}`}
                  data-testid="doctor-filter-milano"
                >
                  Milano
                </button>
                <button
                  onClick={() => setAvailabilityLocation('napoli')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${availabilityLocation === 'napoli' ? 'bg-[#2A9FCB] text-white shadow-md scale-105' : 'text-[#2A9FCB] hover:bg-blue-50 border border-blue-200'}`}
                  data-testid="doctor-filter-napoli"
                >
                  Napoli
                </button>
                <span className="ml-auto text-xs font-medium px-3 py-1 rounded-full" style={{
                  background: availabilityLocation === 'milano' ? '#F97316' : '#2A9FCB',
                  color: 'white'
                }}>
                  {availabilityLocation === 'milano' ? 'Via Santa Sofia 10' : 'Via Posillipo 160'}
                </span>
              </div>
              
              {/* Quick Actions */}
              <div className="flex gap-4 flex-wrap">
                <button
                  onClick={() => setFullMonth('morning', true)}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-50"
                >
                  <Sun className="w-4 h-4" />
                  Attiva tutte le mattine
                </button>
                <button
                  onClick={() => setFullMonth('afternoon', true)}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 bg-indigo-100 text-indigo-800 hover:bg-indigo-200 disabled:opacity-50"
                >
                  <Moon className="w-4 h-4" />
                  Attiva tutti i pomeriggi
                </button>
                <button
                  onClick={() => setFullMonth('morning', false)}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 bg-gray-100 text-[#2A9FCB]/80 hover:bg-gray-200 disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                  Rimuovi mattine
                </button>
                <button
                  onClick={() => setFullMonth('afternoon', false)}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 bg-gray-100 text-[#2A9FCB]/80 hover:bg-gray-200 disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                  Rimuovi pomeriggi
                </button>
              </div>

              {/* Calendar */}
              <div className="rounded-2xl shadow-lg p-6" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
                {/* Month Navigation */}
                <div className="flex items-center justify-between mb-6">
                  <button onClick={prevMonth} className="p-2 rounded-full hover:bg-white/50" style={{color: '#2A9FCB'}}>
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <h2 className="text-xl font-bold" style={{color: '#2A9FCB'}}>
                    {MONTHS[currentMonth - 1]} {currentYear}
                    <span className={`ml-3 text-sm font-semibold px-3 py-1 rounded-full ${availabilityLocation === 'milano' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                      {availabilityLocation === 'milano' ? 'Milano' : 'Napoli'}
                    </span>
                  </h2>
                  <button onClick={nextMonth} className="p-2 rounded-full hover:bg-white/50" style={{color: '#2A9FCB'}}>
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>

                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {WEEKDAYS.map(day => (
                    <div key={day} className="text-center text-sm font-medium py-2" style={{color: '#6B7280'}}>
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-2">
                  {/* Empty cells for offset */}
                  {Array.from({ length: offset }).map((_, i) => (
                    <div key={`empty-${i}`} className="aspect-square" />
                  ))}
                  
                  {/* Day cells */}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const date = new Date(currentYear, currentMonth - 1, day);
                    const dateStr = date.toISOString().split('T')[0];
                    const weekday = date.getDay();
                    const isSunday = weekday === 0;
                    const isPast = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    const isToday = dateStr === todayStr;
                    
                    const dayAvail = availability[dateStr] || {};
                    const morningBookings = bookingsCount[`${dateStr}_morning`] || 0;
                    const afternoonBookings = bookingsCount[`${dateStr}_afternoon`] || 0;
                    const totalBookings = morningBookings + afternoonBookings;
                    const hasAvailability = dayAvail.morning_available || dayAvail.afternoon_available;
                    
                    return (
                      <div
                        key={day}
                        className={`rounded-xl p-2 transition-all ${isSunday || isPast ? 'opacity-40' : ''} ${isToday ? 'ring-2 ring-offset-2' : ''}`}
                        style={{
                          background: '#fff',
                          minHeight: '90px',
                          '--tw-ring-color': '#2A9FCB'
                        }}
                      >
                        {/* Clickable day number */}
                        <button
                          onClick={() => !isSunday && !isPast && hasAvailability && openDayView(dateStr, hasAvailability)}
                          disabled={isSunday || isPast || !hasAvailability}
                          className={`text-sm font-medium mb-2 w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                            hasAvailability && totalBookings > 0 
                              ? 'bg-[#2A9FCB] text-white hover:bg-[#2A9FCB]/80 cursor-pointer' 
                              : hasAvailability
                                ? 'hover:bg-[#2A9FCB]/10 cursor-pointer'
                                : ''
                          }`}
                          style={{color: hasAvailability && totalBookings > 0 ? '#fff' : '#2A9FCB'}}
                          title={hasAvailability ? `Clicca per vedere gli appuntamenti del ${day}` : 'Nessuna disponibilità'}
                          data-testid={`day-${dateStr}`}
                        >
                          {day}
                        </button>
                        
                        {!isSunday && !isPast && (
                          <div className="space-y-1">
                            {/* Morning */}
                            <button
                              onClick={() => toggleAvailability(dateStr, 'morning')}
                              disabled={saving}
                              className={`w-full py-1 px-2 rounded text-xs flex items-center justify-between ${
                                dayAvail.morning_available 
                                  ? 'bg-amber-100 text-amber-800' 
                                  : 'bg-gray-100 text-[#2A9FCB]/60'
                              }`}
                            >
                              <div className="flex items-center gap-1">
                                <Sun className="w-3 h-3" />
                                <span>Matt</span>
                              </div>
                              {dayAvail.morning_available && morningBookings > 0 && (
                                <span className="bg-amber-500 text-white px-1 rounded text-[10px]">{morningBookings}</span>
                              )}
                            </button>
                            
                            {/* Afternoon */}
                            <button
                              onClick={() => toggleAvailability(dateStr, 'afternoon')}
                              disabled={saving}
                              className={`w-full py-1 px-2 rounded text-xs flex items-center justify-between ${
                                dayAvail.afternoon_available 
                                  ? 'bg-indigo-100 text-indigo-800' 
                                  : 'bg-gray-100 text-[#2A9FCB]/60'
                              }`}
                            >
                              <div className="flex items-center gap-1">
                                <Moon className="w-3 h-3" />
                                <span>Pom</span>
                              </div>
                              {dayAvail.afternoon_available && afternoonBookings > 0 && (
                                <span className="bg-indigo-500 text-white px-1 rounded text-[10px]">{afternoonBookings}</span>
                              )}
                            </button>
                          </div>
                        )}
                        
                        {isSunday && <p className="text-xs text-[#2A9FCB]/60">Chiuso</p>}
                      </div>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="mt-6 flex flex-wrap gap-4 text-xs justify-center">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-amber-100"></div>
                    <span>Mattina attiva (9:30-12:30)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-indigo-100"></div>
                    <span>Pomeriggio attivo (15:30-18:30)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-gray-100"></div>
                    <span>Non disponibile</span>
                  </div>
                </div>

                <p className="mt-4 text-center text-sm text-[#2A9FCB]/70">
                  Clicca su &quot;Matt&quot; o &quot;Pom&quot; per attivare/disattivare la disponibilità per <strong>{availabilityLocation === 'milano' ? 'Milano' : 'Napoli'}</strong>. I pazienti potranno prenotare solo negli slot attivi.
                </p>
              </div>
            </div>
          )}

          {/* Today Tab */}
          {activeTab === 'today' && (
            <div className="rounded-2xl p-6 shadow-sm" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{color: '#2A9FCB'}}>
                <Clock className="w-5 h-5" />
                Appuntamenti di Oggi ({todayAppointments.length})
              </h3>
              
              {todayAppointments.length === 0 ? (
                <p className="text-[#2A9FCB]/70 text-center py-8">Nessun appuntamento per oggi</p>
              ) : (
                <div className="space-y-3">
                  {todayAppointments.map(appt => (
                    <div key={appt.id} className="flex items-center gap-4 p-4 bg-white rounded-xl">
                      <div className="w-16 text-center">
                        <p className="text-lg font-bold" style={{color: '#2A9FCB'}}>
                          {new Date(appt.appointment_date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold" style={{color: '#2A9FCB'}}>{appt.reason}</p>
                        <p className="text-sm text-[#2A9FCB]/70">Paziente ID: {appt.patient_id.slice(0, 8)}...</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {appt.appointment_type === 'telemedicine' && (
                          <span className="px-2 py-1 rounded-full  text-xs font-medium bg-purple-100 text-purple-700 flex items-center gap-1">
                            <Video className="w-3 h-3" />
                            Tele
                          </span>
                        )}
                        <span className="px-3 py-1 rounded-full  text-xs font-medium" style={{background: '#DBEAFE', color: '#1E40AF'}}>
                          {appt.appointment_type === 'telemedicine' ? '10 min' : '20 min'}
                        </span>
                        <button
                          onClick={() => navigate(`/cartella-clinica/${appt.patient_id}`)}
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                          title="Apri Cartella Clinica"
                          data-testid={`open-ehr-${appt.id}`}
                        >
                          <FileText className="w-5 h-5" style={{color: '#2A9FCB'}} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && (
            <div className="space-y-6">
              {/* Period Selector + Toggle */}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[#2A9FCB]/70">Periodo:</span>
                  {['month', '3months', '6months', 'year', 'max'].map(p => (
                    <button
                      key={p}
                      onClick={() => setChartPeriod(p)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                        chartPeriod === p 
                          ? 'bg-[#2A9FCB] text-white' 
                          : 'bg-white border border-[#2A9FCB]/30 text-[#2A9FCB] hover:bg-[#2A9FCB]/10'
                      }`}
                    >
                      {{month: '1M', '3months': '3M', '6months': '6M', year: '1A', max: 'Max'}[p]}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowCompanyStats(false)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      !showCompanyStats ? 'bg-[#2A9FCB] text-white' : 'bg-white border text-[#2A9FCB]'
                    }`}
                  >
                    Le Mie Statistiche
                  </button>
                  <button
                    onClick={() => setShowCompanyStats(true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      showCompanyStats ? 'bg-[#2A9FCB] text-white' : 'bg-white border text-[#2A9FCB]'
                    }`}
                  >
                    Totale Azienda
                  </button>
                  <button onClick={() => loadChartData(chartPeriod)} disabled={loadingChart} className="p-2 rounded-lg border hover:bg-gray-50">
                    <RefreshCcw className={`w-4 h-4 text-[#2A9FCB] ${loadingChart ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>

              {loadingChart ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-[#2A9FCB]" />
                </div>
              ) : !showCompanyStats && chartData ? (
                <>
                  {/* Personal Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="rounded-2xl p-6 shadow-sm" style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}>
                      <Eye className="w-8 h-8 text-white/80 mb-2" />
                      <p className="text-3xl font-bold text-white">{chartData.summary?.total_visits || 0}</p>
                      <p className="text-sm text-white/80">Visite {chartData.period_label}</p>
                      {chartData.summary?.visits_trend_percent !== 0 && (
                        <div className={`flex items-center gap-1 mt-2 text-xs ${chartData.summary.visits_trend_percent > 0 ? 'text-green-200' : 'text-red-200'}`}>
                          {chartData.summary.visits_trend_percent > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                          {Math.abs(chartData.summary.visits_trend_percent)}% vs periodo precedente
                        </div>
                      )}
                    </div>
                    <div className="rounded-2xl p-6 shadow-sm bg-white border">
                      <DollarSign className="w-8 h-8 mb-2 text-[#2A9FCB]" />
                      <p className="text-3xl font-bold text-[#2A9FCB]">€{(chartData.summary?.total_earnings || 0).toLocaleString()}</p>
                      <p className="text-sm text-[#2A9FCB]/70">Guadagni {chartData.period_label}</p>
                    </div>
                    <div className="rounded-2xl p-6 shadow-sm bg-white border">
                      <Calendar className="w-8 h-8 mb-2 text-[#2A9FCB]" />
                      <p className="text-3xl font-bold text-[#2A9FCB]">{chartData.summary?.avg_visits_per_day || 0}</p>
                      <p className="text-sm text-[#2A9FCB]/70">Media visite/giorno</p>
                    </div>
                    <div className="rounded-2xl p-6 shadow-sm bg-white border">
                      <TrendingUp className="w-8 h-8 mb-2 text-[#2A9FCB]" />
                      <p className="text-3xl font-bold text-[#2A9FCB]">€{(chartData.summary?.avg_earnings_per_visit || 0).toFixed(0)}</p>
                      <p className="text-sm text-[#2A9FCB]/70">Media €/visita</p>
                    </div>
                  </div>

                  {/* Visits Chart */}
                  <div className="rounded-2xl p-6 shadow-sm bg-white border">
                    <h3 className="text-lg font-bold mb-4 text-[#2A9FCB]">📈 Andamento Visite</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData.chart_data}>
                          <defs>
                            <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#2A9FCB" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#2A9FCB" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="label" tick={{fontSize: 12}} stroke="#9CA3AF" />
                          <YAxis tick={{fontSize: 12}} stroke="#9CA3AF" />
                          <Tooltip 
                            contentStyle={{background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px'}}
                            formatter={(value) => [value, 'Visite']}
                          />
                          <Area type="monotone" dataKey="visits" stroke="#2A9FCB" strokeWidth={2} fillOpacity={1} fill="url(#colorVisits)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Earnings Chart */}
                  <div className="rounded-2xl p-6 shadow-sm bg-white border">
                    <h3 className="text-lg font-bold mb-4 text-[#2A9FCB]">💰 Andamento Guadagni</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData.chart_data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="label" tick={{fontSize: 12}} stroke="#9CA3AF" />
                          <YAxis tick={{fontSize: 12}} stroke="#9CA3AF" tickFormatter={(v) => `€${v}`} />
                          <Tooltip 
                            contentStyle={{background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px'}}
                            formatter={(value) => [`€${value}`, 'Guadagni']}
                          />
                          <Bar dataKey="earnings" fill="#2A9FCB" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              ) : showCompanyStats && companyChartData ? (
                <>
                  {/* Company Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div className="rounded-2xl p-6 shadow-sm" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
                      <Building2 className="w-8 h-8 text-white/80 mb-2" />
                      <p className="text-3xl font-bold text-white">{companyChartData.summary?.total_visits || 0}</p>
                      <p className="text-sm text-white/80">Visite Totali</p>
                    </div>
                    <div className="rounded-2xl p-6 shadow-sm bg-white border">
                      <UserPlus className="w-8 h-8 mb-2 text-purple-500" />
                      <p className="text-3xl font-bold text-purple-600">{companyChartData.summary?.total_new_patients || 0}</p>
                      <p className="text-sm text-purple-600/70">Nuovi Pazienti</p>
                    </div>
                    <div className="rounded-2xl p-6 shadow-sm bg-white border">
                      <DollarSign className="w-8 h-8 mb-2 text-emerald-500" />
                      <p className="text-3xl font-bold text-emerald-600">€{(companyChartData.summary?.total_revenue || 0).toLocaleString()}</p>
                      <p className="text-sm text-emerald-600/70">Fatturato Totale</p>
                    </div>
                    <div className="rounded-2xl p-6 shadow-sm bg-white border">
                      <Eye className="w-8 h-8 mb-2 text-blue-500" />
                      <p className="text-3xl font-bold text-blue-600">{companyChartData.summary?.active_doctors || 0}</p>
                      <p className="text-sm text-blue-600/70">Oculisti Attivi</p>
                    </div>
                    <div className="rounded-2xl p-6 shadow-sm bg-white border">
                      <Users className="w-8 h-8 mb-2 text-indigo-500" />
                      <p className="text-3xl font-bold text-indigo-600">{companyChartData.summary?.active_ortottisti || 0}</p>
                      <p className="text-sm text-indigo-600/70">Ortottisti Attivi</p>
                    </div>
                  </div>

                  {/* Company Chart */}
                  <div className="rounded-2xl p-6 shadow-sm bg-white border">
                    <h3 className="text-lg font-bold mb-4 text-purple-700">📊 Andamento Azienda - {companyChartData.period_label}</h3>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={companyChartData.chart_data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="label" tick={{fontSize: 12}} stroke="#9CA3AF" />
                          <YAxis yAxisId="left" tick={{fontSize: 12}} stroke="#9CA3AF" />
                          <YAxis yAxisId="right" orientation="right" tick={{fontSize: 12}} stroke="#9CA3AF" tickFormatter={(v) => `€${v}`} />
                          <Tooltip 
                            contentStyle={{background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px'}}
                          />
                          <Line yAxisId="left" type="monotone" dataKey="visits" stroke="#667eea" strokeWidth={2} dot={{r: 3}} name="Visite" />
                          <Line yAxisId="left" type="monotone" dataKey="new_patients" stroke="#10B981" strokeWidth={2} dot={{r: 3}} name="Nuovi Pazienti" />
                          <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#F59E0B" strokeWidth={2} dot={{r: 3}} name="Fatturato €" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex justify-center gap-6 mt-4">
                      <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#667eea]"></div><span className="text-xs">Visite</span></div>
                      <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#10B981]"></div><span className="text-xs">Nuovi Pazienti</span></div>
                      <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#F59E0B]"></div><span className="text-xs">Fatturato</span></div>
                    </div>
                  </div>
                </>
              ) : stats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="rounded-2xl p-6 shadow-sm" style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}>
                    <Eye className="w-8 h-8 text-white/80 mb-4" />
                    <p className="text-3xl font-bold text-white">{stats.total_visits}</p>
                    <p className="text-sm text-white/80">Visite totali</p>
                  </div>
                  <div className="rounded-2xl p-6 shadow-sm bg-white" style={{border: '1px solid #E5E7EB'}}>
                    <Calendar className="w-8 h-8 mb-4" style={{color: '#2A9FCB'}} />
                    <p className="text-3xl font-bold" style={{color: '#2A9FCB'}}>{stats.visits_this_month}</p>
                    <p className="text-sm text-[#2A9FCB]/70">Visite questo mese</p>
                  </div>
                  <div className="rounded-2xl p-6 shadow-sm bg-white" style={{border: '1px solid #E5E7EB'}}>
                    <UserPlus className="w-8 h-8 mb-4" style={{color: '#2A9FCB'}} />
                    <p className="text-3xl font-bold" style={{color: '#2A9FCB'}}>{stats.new_patients_this_month}</p>
                    <p className="text-sm text-[#2A9FCB]/70">Nuovi pazienti</p>
                  </div>
                  <div className="rounded-2xl p-6 shadow-sm bg-white" style={{border: '1px solid #E5E7EB'}}>
                    <DollarSign className="w-8 h-8 mb-4" style={{color: '#2A9FCB'}} />
                    <p className="text-3xl font-bold" style={{color: '#2A9FCB'}}>{formatCurrency(stats.earnings.this_month)}</p>
                    <p className="text-sm text-[#2A9FCB]/70">Guadagni mese</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pazienti Tab */}
          {activeTab === 'pazienti' && (
            <div className="space-y-6">
              <div className="rounded-2xl shadow-lg p-6 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold" style={{color: '#2A9FCB'}}>
                    <Users className="w-6 h-6 inline mr-2" />
                    Database Pazienti
                  </h2>
                  <button
                    onClick={() => setShowRegisterPatientModal(true)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:shadow-md"
                    style={{background: '#2A9FCB'}}
                    data-testid="doctor-register-patient-btn"
                  >
                    <UserPlus className="w-4 h-4" /> Registra Nuovo Paziente
                  </button>
                </div>
                
                {/* Search Bar */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2A9FCB]/60" />
                  <input
                    type="text"
                    placeholder="Cerca paziente per nome, cognome, email, telefono o codice fiscale..."
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2"
                    style={{ focusRing: '#2A9FCB' }}
                    data-testid="patient-search-input"
                  />
                </div>

                {/* Advanced Filters */}
                <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Filter className="w-4 h-4 text-[#2A9FCB]/70" />
                    <span className="text-sm font-semibold text-[#2A9FCB]/80">Filtri Avanzati</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {/* Sesso */}
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 block mb-1">Sesso</label>
                      <select
                        value={patientFilters?.gender || ''}
                        onChange={(e) => setPatientFilters(prev => ({...prev, gender: e.target.value}))}
                        className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                        data-testid="filter-gender"
                      >
                        <option value="">Tutti</option>
                        <option value="M">Maschio</option>
                        <option value="F">Femmina</option>
                      </select>
                    </div>
                    
                    {/* Età DA */}
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 block mb-1">Età da</label>
                      <input
                        type="number"
                        placeholder="0"
                        min="0"
                        max="120"
                        value={patientFilters?.ageMin || ''}
                        onChange={(e) => setPatientFilters(prev => ({...prev, ageMin: e.target.value}))}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        data-testid="filter-age-min"
                      />
                    </div>
                    
                    {/* Età A */}
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 block mb-1">Età a</label>
                      <input
                        type="number"
                        placeholder="120"
                        min="0"
                        max="120"
                        value={patientFilters?.ageMax || ''}
                        onChange={(e) => setPatientFilters(prev => ({...prev, ageMax: e.target.value}))}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        data-testid="filter-age-max"
                      />
                    </div>
                    
                    {/* Etnia/Razza */}
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 block mb-1">Etnia/Razza</label>
                      <select
                        value={patientFilters?.ethnicity || ''}
                        onChange={(e) => setPatientFilters(prev => ({...prev, ethnicity: e.target.value}))}
                        className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                        data-testid="filter-ethnicity"
                      >
                        <option value="">Tutte</option>
                        <option value="caucasica">Caucasica</option>
                        <option value="afro">Africana/Afroamericana</option>
                        <option value="asiatica">Asiatica</option>
                        <option value="ispanica">Ispanica/Latina</option>
                        <option value="mediorientale">Mediorientale</option>
                        <option value="nativo_americana">Nativo Americana</option>
                        <option value="pacifica">Oceanica/Pacifica</option>
                        <option value="mista">Mista</option>
                        <option value="altro">Altro</option>
                      </select>
                    </div>
                    
                    {/* Anno nascita DA */}
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 block mb-1">Anno nascita da</label>
                      <input
                        type="number"
                        placeholder="1950"
                        min="1900"
                        max={new Date().getFullYear()}
                        value={patientFilters?.birthYearFrom || ''}
                        onChange={(e) => setPatientFilters(prev => ({...prev, birthYearFrom: e.target.value}))}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        data-testid="filter-birth-from"
                      />
                    </div>
                    
                    {/* Anno nascita A */}
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 block mb-1">Anno nascita a</label>
                      <input
                        type="number"
                        placeholder="2024"
                        min="1900"
                        max={new Date().getFullYear()}
                        value={patientFilters?.birthYearTo || ''}
                        onChange={(e) => setPatientFilters(prev => ({...prev, birthYearTo: e.target.value}))}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        data-testid="filter-birth-to"
                      />
                    </div>
                    
                    {/* Ultima visita DA */}
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 block mb-1">Ultima visita da</label>
                      <input
                        type="date"
                        value={patientFilters?.lastVisitFrom || ''}
                        onChange={(e) => setPatientFilters(prev => ({...prev, lastVisitFrom: e.target.value}))}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        data-testid="filter-visit-from"
                      />
                    </div>
                    
                    {/* Ultima visita A */}
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 block mb-1">Ultima visita a</label>
                      <input
                        type="date"
                        value={patientFilters?.lastVisitTo || ''}
                        onChange={(e) => setPatientFilters(prev => ({...prev, lastVisitTo: e.target.value}))}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        data-testid="filter-visit-to"
                      />
                    </div>
                    
                    {/* Tag Diagnosi */}
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 block mb-1">Tag Diagnosi</label>
                      <input
                        type="text"
                        placeholder="es. glaucoma, cataratta"
                        value={patientFilters?.diagnosisTag || ''}
                        onChange={(e) => setPatientFilters(prev => ({...prev, diagnosisTag: e.target.value}))}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        data-testid="filter-diagnosis"
                      />
                    </div>
                  </div>
                  
                  {/* Filter actions */}
                  <div className="flex justify-between items-center mt-3">
                    <p className="text-xs text-[#2A9FCB]/70">
                      {filteredPatients.length} pazienti trovati
                    </p>
                    <button
                      onClick={() => {
                        setPatientFilters({});
                        setPatientSearch('');
                      }}
                      className="text-sm text-red-500 hover:text-red-700"
                    >
                      Resetta filtri
                    </button>
                  </div>
                </div>

                {/* Patients List */}
                {loadingPatients ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto" />
                    <p className="text-[#2A9FCB]/70 mt-2">Caricamento pazienti...</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold text-[#2A9FCB]/80">Paziente</th>
                          <th className="text-left py-3 px-4 font-semibold text-[#2A9FCB]/80">Sesso</th>
                          <th className="text-left py-3 px-4 font-semibold text-[#2A9FCB]/80">Data Nascita</th>
                          <th className="text-left py-3 px-4 font-semibold text-[#2A9FCB]/80">Età</th>
                          <th className="text-left py-3 px-4 font-semibold text-[#2A9FCB]/80">Contatti</th>
                          <th className="text-left py-3 px-4 font-semibold text-[#2A9FCB]/80">Ultima Visita</th>
                          <th className="text-left py-3 px-4 font-semibold text-[#2A9FCB]/80">Diagnosi</th>
                          <th className="text-center py-3 px-4 font-semibold text-[#2A9FCB]/80">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPatients.map(patient => (
                            <tr 
                              key={patient.id} 
                              className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                              onClick={() => openPatientProfile(patient)}
                            >
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold" style={{background: patient.gender === 'F' ? '#EC4899' : '#2A9FCB'}}>
                                    {patient.first_name?.[0]}{patient.last_name?.[0]}
                                  </div>
                                  <div>
                                    <p className="font-medium">{patient.first_name} {patient.last_name}</p>
                                    <p className="text-xs text-[#2A9FCB]/60">
                                      {patient.codice_fiscale || patient.email?.split('@')[0]}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${patient.gender === 'F' ? 'bg-pink-100 text-pink-700' : patient.gender === 'M' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                  {patient.gender === 'F' ? '♀ F' : patient.gender === 'M' ? '♂ M' : '-'}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-sm text-[#2A9FCB]/80">
                                {patient.date_of_birth 
                                  ? new Date(patient.date_of_birth).toLocaleDateString('it-IT')
                                  : '-'}
                              </td>
                              <td className="py-3 px-4 text-sm text-[#2A9FCB]/80">
                                {patient.date_of_birth 
                                  ? Math.floor((new Date() - new Date(patient.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))
                                  : '-'}
                              </td>
                              <td className="py-3 px-4">
                                <p className="text-sm text-[#2A9FCB]/80">{patient.email}</p>
                                <p className="text-xs text-[#2A9FCB]/60">{patient.phone || '-'}</p>
                              </td>
                              <td className="py-3 px-4">
                                {patient.last_visit_date ? (
                                  <div>
                                    <p className="text-sm font-medium text-[#2A9FCB]">
                                      {new Date(patient.last_visit_date).toLocaleDateString('it-IT')}
                                    </p>
                                    {patient.last_visit?.diagnosis && (
                                      <p className="text-xs text-[#2A9FCB]/70 truncate max-w-32">
                                        {patient.last_visit.diagnosis}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-[#2A9FCB]/60 text-sm">Mai visitato</span>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex flex-wrap gap-1">
                                  {(patient.diagnosis_tags || []).slice(0, 3).map((tag, idx) => (
                                    <span 
                                      key={idx}
                                      className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full "
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                  {(patient.diagnosis_tags || []).length > 3 && (
                                    <span className="text-xs text-[#2A9FCB]/60">+{patient.diagnosis_tags.length - 3}</span>
                                  )}
                                  {(!patient.diagnosis_tags || patient.diagnosis_tags.length === 0) && (
                                    <span className="text-[#2A9FCB]/60 text-xs">-</span>
                                  )}
                                </div>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={(e) => openPatientProfile(patient, e)}
                                    className="px-3 py-2 rounded-lg font-medium flex items-center gap-1 border border-[#2A9FCB] text-[#2A9FCB] hover:bg-[#2A9FCB]/10"
                                    title="Modifica Profilo"
                                  >
                                    <Users className="w-4 h-4" />
                                    Profilo
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/cartella-clinica/${patient.id}`);
                                    }}
                                    className="px-3 py-2 rounded-lg text-white font-medium flex items-center gap-1"
                                    style={{background: '#2A9FCB'}}
                                    title="Apri Cartella Clinica"
                                  >
                                    <FileText className="w-4 h-4" />
                                    Cartella
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>

                    {filteredPatients.length === 0 && (
                      <p className="text-center text-[#2A9FCB]/70 py-8">
                        {patientSearch ? 'Nessun paziente trovato con questi criteri' : 'Nessun paziente registrato'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sala d'Attesa Tab */}
          {activeTab === 'sala-attesa' && (
            <div className="space-y-6">
              {/* Alert Banner */}
              {showAlertBanner && (
                <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-4 shadow-lg animate-pulse">
                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <Bell className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-bold text-lg">🔔 Nuovo Paziente!</p>
                        <p className="text-sm opacity-90">Un paziente è arrivato in sala d&apos;attesa</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setShowAlertBanner(false)}
                      className="p-2 hover:bg-white/20 rounded-full"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Header con navigazione date */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold" style={{color: '#2A9FCB'}}>
                    🏥 Sala d&apos;Attesa Virtuale
                  </h2>
                  <div className="flex items-center gap-4">
                    {/* Alert Controls */}
                    <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-lg">
                      <button
                        onClick={() => setAlertsEnabled(!alertsEnabled)}
                        className={`p-2 rounded-lg transition-colors ${alertsEnabled ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-[#2A9FCB]/60'}`}
                        title={alertsEnabled ? 'Disattiva notifiche' : 'Attiva notifiche'}
                        data-testid="toggle-alerts"
                      >
                        {alertsEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={() => {
                          setSoundEnabled(!soundEnabled);
                          if (!soundEnabled) playAlertSound(); // Test sound
                        }}
                        className={`p-2 rounded-lg transition-colors ${soundEnabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-[#2A9FCB]/60'}`}
                        title={soundEnabled ? 'Disattiva suono' : 'Attiva suono'}
                        data-testid="toggle-sound"
                      >
                        {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                      </button>
                    </div>
                    
                    <button
                      onClick={() => changeSalaAttesaDate(-1)}
                      className="p-2 rounded-full hover:bg-gray-100"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div className="text-center">
                      <input 
                        type="date" 
                        value={salaAttesaDate}
                        onChange={(e) => setSalaAttesaDate(e.target.value)}
                        className="border rounded-lg px-4 py-2 text-lg font-medium"
                        style={{color: '#2A9FCB'}}
                      />
                    </div>
                    <button
                      onClick={() => changeSalaAttesaDate(1)}
                      className="p-2 rounded-full hover:bg-gray-100"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setSalaAttesaDate(new Date().toISOString().split('T')[0])}
                      className="px-4 py-2 rounded-lg text-white"
                      style={{background: '#2A9FCB'}}
                    >
                      Oggi
                    </button>
                  </div>
                </div>

                {/* Filtri Dottore e Stanza */}
                <div className="flex flex-wrap items-center gap-4 mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-[#2A9FCB]/70" />
                    <span className="text-sm font-medium text-[#2A9FCB]/80">Filtri:</span>
                  </div>
                  
                  {/* Filtro Dottore */}
                  <select
                    value={filterDoctor}
                    onChange={(e) => setFilterDoctor(e.target.value)}
                    className="border rounded-lg px-3 py-2 text-sm bg-white"
                    data-testid="filter-doctor-select"
                  >
                    <option value="">Tutti i Medici</option>
                    {doctorsToday.map(doc => (
                      <option key={doc.id} value={doc.id}>
                        Dott. {doc.last_name} {doc.first_name}
                      </option>
                    ))}
                  </select>
                  
                  {/* Filtro Stanza */}
                  <select
                    value={filterRoom}
                    onChange={(e) => setFilterRoom(e.target.value)}
                    className="border rounded-lg px-3 py-2 text-sm bg-white"
                    data-testid="filter-room-select"
                  >
                    <option value="">Tutte le Stanze</option>
                    {roomsToday.map(room => (
                      <option key={room} value={room}>{room}</option>
                    ))}
                  </select>
                  
                  {/* Toggle vista */}
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-sm text-[#2A9FCB]/70">Vista:</span>
                    <button
                      onClick={() => setViewMode('status')}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${viewMode === 'status' ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-[#2A9FCB]/80'}`}
                    >
                      Per Stato
                    </button>
                    <button
                      onClick={() => setViewMode('room')}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${viewMode === 'room' ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-[#2A9FCB]/80'}`}
                    >
                      Per Stanza
                    </button>
                  </div>
                  
                  {(filterDoctor || filterRoom) && (
                    <button
                      onClick={resetFilters}
                      className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <X className="w-4 h-4" />
                      Reset
                    </button>
                  )}
                </div>

                {/* QR Code */}
                {qrCodeUrl && (
                  <div className="flex items-center gap-4 p-4 rounded-lg mt-4" style={{background: '#F0FAF5'}}>
                    <img src={qrCodeUrl} alt="QR Check-in" className="w-24 h-24 rounded-lg" />
                    <div>
                      <h3 className="font-semibold" style={{color: '#2A9FCB'}}>QR Code Check-in del Giorno</h3>
                      <p className="text-sm text-[#2A9FCB]/80">I pazienti possono scansionare questo QR all&apos;ingresso per fare il check-in automatico.</p>
                      <p className="text-xs text-[#2A9FCB]/60 mt-1">Stampa e posiziona all&apos;accettazione</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Riepilogo Stanze */}
              {roomsToday.length > 0 && (
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Building2 className="w-5 h-5" style={{color: '#2A9FCB'}} />
                    Stanze Attive Oggi
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {roomsToday.map(room => {
                      const roomConfig = ROOM_COLORS[room] || { bg: '#F3F4F6', text: '#4B5563', border: '#D1D5DB' };
                      const roomDoctors = doctorsToday.filter(d => d.room === room);
                      return (
                        <div 
                          key={room} 
                          className="flex items-center gap-2 px-4 py-2 rounded-lg border-2"
                          style={{background: roomConfig.bg, borderColor: roomConfig.border}}
                        >
                          <Building2 className="w-4 h-4" style={{color: roomConfig.text}} />
                          <div>
                            <span className="font-medium" style={{color: roomConfig.text}}>{room}</span>
                            <div className="text-xs" style={{color: roomConfig.text}}>
                              {roomDoctors.map(d => `Dott. ${d.last_name}`).join(', ')}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Legenda */}
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h3 className="font-semibold mb-3">📋 Legenda Stati</h3>
                <div className="flex flex-wrap gap-4">
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <div key={key} className="flex items-center gap-2 px-3 py-1 rounded-full " style={{background: config.bg}}>
                      <span>{config.emoji}</span>
                      <span className="text-sm font-medium" style={{color: config.color}}>{config.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Loading */}
              {loadingSalaAttesa && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin" style={{color: '#2A9FCB'}} />
                </div>
              )}

              {/* Vista per Stato */}
              {!loadingSalaAttesa && salaAttesaData && viewMode === 'status' && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* In Attesa */}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-4 border-b" style={{background: STATUS_CONFIG.scheduled.bg}}>
                      <h3 className="font-semibold flex items-center gap-2" style={{color: STATUS_CONFIG.scheduled.color}}>
                        {STATUS_CONFIG.scheduled.emoji} In Attesa ({salaAttesaData.by_status?.scheduled?.length || 0})
                      </h3>
                    </div>
                    <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
                      {salaAttesaData.by_status?.scheduled?.map(apt => (
                        <div key={apt.id} className="p-3 rounded-lg border" style={{borderColor: STATUS_CONFIG.scheduled.color}}>
                          <div className="flex justify-between items-start mb-1">
                            <p className="font-medium">{apt.patient?.first_name} {apt.patient?.last_name}</p>
                            {apt.room && (
                              <span className="text-xs px-2 py-0.5 rounded-full " style={{background: ROOM_COLORS[apt.room]?.bg || '#F3F4F6', color: ROOM_COLORS[apt.room]?.text || '#4B5563'}}>
                                {apt.room}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#2A9FCB]/70">{new Date(apt.appointment_date).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}</p>
                          {apt.doctor && <p className="text-xs text-[#2A9FCB]/60">Dott. {apt.doctor.last_name}</p>}
                          <p className="text-xs text-[#2A9FCB]/60">{apt.reason || 'Visita'}</p>
                        </div>
                      ))}
                      {(!salaAttesaData.by_status?.scheduled || salaAttesaData.by_status.scheduled.length === 0) && (
                        <p className="text-center text-[#2A9FCB]/60 text-sm py-4">Nessun paziente</p>
                      )}
                    </div>
                  </div>

                  {/* Arrivato (Check-in) */}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-4 border-b" style={{background: STATUS_CONFIG.checked_in.bg}}>
                      <h3 className="font-semibold flex items-center gap-2" style={{color: STATUS_CONFIG.checked_in.color}}>
                        {STATUS_CONFIG.checked_in.emoji} Arrivati - Attesa Pagamento ({salaAttesaData.by_status?.checked_in?.length || 0})
                      </h3>
                    </div>
                    <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
                      {salaAttesaData.by_status?.checked_in?.map(apt => (
                        <div key={apt.id} className="p-3 rounded-lg border" style={{borderColor: STATUS_CONFIG.checked_in.color}}>
                          <div className="flex justify-between items-start mb-1">
                            <p className="font-medium">{apt.patient?.first_name} {apt.patient?.last_name}</p>
                            {apt.room && (
                              <span className="text-xs px-2 py-0.5 rounded-full " style={{background: ROOM_COLORS[apt.room]?.bg || '#F3F4F6', color: ROOM_COLORS[apt.room]?.text || '#4B5563'}}>
                                {apt.room}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#2A9FCB]/70">Check-in: {apt.checked_in_at ? new Date(apt.checked_in_at).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'}) : '-'}</p>
                          {apt.doctor && <p className="text-xs text-[#2A9FCB]/60 mb-1">Dott. {apt.doctor.last_name}</p>}
                          <button
                            onClick={() => updateAppointmentStatus(apt.id, 'paid')}
                            className="mt-2 w-full px-3 py-1 rounded text-white text-sm flex items-center justify-center gap-1"
                            style={{background: STATUS_CONFIG.paid.color}}
                          >
                            <CheckCircle className="w-4 h-4" /> Conferma Pagamento
                          </button>
                        </div>
                      ))}
                      {(!salaAttesaData.by_status?.checked_in || salaAttesaData.by_status.checked_in.length === 0) && (
                        <p className="text-center text-[#2A9FCB]/60 text-sm py-4">Nessun paziente</p>
                      )}
                    </div>
                  </div>

                  {/* Pagato - Pronto */}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-4 border-b" style={{background: STATUS_CONFIG.paid.bg}}>
                      <h3 className="font-semibold flex items-center gap-2" style={{color: STATUS_CONFIG.paid.color}}>
                        {STATUS_CONFIG.paid.emoji} Pronti per Visita ({salaAttesaData.by_status?.paid?.length || 0})
                      </h3>
                    </div>
                    <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
                      {salaAttesaData.by_status?.paid?.map(apt => (
                        <div key={apt.id} className="p-3 rounded-lg border" style={{borderColor: STATUS_CONFIG.paid.color}}>
                          <div className="flex justify-between items-start mb-1">
                            <p className="font-medium">{apt.patient?.first_name} {apt.patient?.last_name}</p>
                            {apt.assigned_room && (
                              <span className="text-xs px-2 py-0.5 rounded-full  bg-green-100 text-green-700">
                                📢 Stanza {apt.assigned_room}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#2A9FCB]/70">Pagato: {apt.paid_at ? new Date(apt.paid_at).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'}) : '-'}</p>
                          {apt.doctor && <p className="text-xs text-[#2A9FCB]/60 mb-2">Dott. {apt.doctor.last_name}</p>}
                          
                          {/* Call to Room Buttons */}
                          <div className="flex gap-2 mb-2">
                            <button
                              onClick={() => callPatientToRoom(apt.id, 'A', apt.patient_id)}
                              className="flex-1 px-3 py-1.5 rounded text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                              title="Chiama paziente in Stanza A"
                            >
                              📢 Stanza A
                            </button>
                            <button
                              onClick={() => callPatientToRoom(apt.id, 'B', apt.patient_id)}
                              className="flex-1 px-3 py-1.5 rounded text-sm font-medium bg-purple-500 text-white hover:bg-purple-600 transition-colors"
                              title="Chiama paziente in Stanza B"
                            >
                              📢 Stanza B
                            </button>
                          </div>
                          
                          <button
                            onClick={() => updateAppointmentStatus(apt.id, 'in_progress')}
                            className="w-full px-3 py-1 rounded text-white text-sm flex items-center justify-center gap-1"
                            style={{background: STATUS_CONFIG.in_progress.color}}
                          >
                            <Play className="w-4 h-4" /> Inizia Visita
                          </button>
                        </div>
                      ))}
                      {(!salaAttesaData.by_status?.paid || salaAttesaData.by_status.paid.length === 0) && (
                        <p className="text-center text-[#2A9FCB]/60 text-sm py-4">Nessun paziente</p>
                      )}
                    </div>
                  </div>

                  {/* In Visita */}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-4 border-b" style={{background: STATUS_CONFIG.in_progress.bg}}>
                      <h3 className="font-semibold flex items-center gap-2" style={{color: STATUS_CONFIG.in_progress.color}}>
                        {STATUS_CONFIG.in_progress.emoji} In Visita ({salaAttesaData.by_status?.in_progress?.length || 0})
                      </h3>
                    </div>
                    <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
                      {salaAttesaData.by_status?.in_progress?.map(apt => (
                        <div key={apt.id} className="p-3 rounded-lg border" style={{borderColor: STATUS_CONFIG.in_progress.color}}>
                          <div className="flex justify-between items-start mb-1">
                            <p className="font-medium">{apt.patient?.first_name} {apt.patient?.last_name}</p>
                            {apt.room && (
                              <span className="text-xs px-2 py-0.5 rounded-full " style={{background: ROOM_COLORS[apt.room]?.bg || '#F3F4F6', color: ROOM_COLORS[apt.room]?.text || '#4B5563'}}>
                                {apt.room}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#2A9FCB]/70">Iniziata: {apt.visit_started_at ? new Date(apt.visit_started_at).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'}) : '-'}</p>
                          {apt.doctor && <p className="text-xs text-[#2A9FCB]/60 mb-1">Dott. {apt.doctor.last_name}</p>}
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={() => navigate(`/cartella-clinica/${apt.patient_id}`)}
                              className="flex-1 px-3 py-1 rounded border text-sm flex items-center justify-center gap-1"
                              style={{borderColor: '#2A9FCB', color: '#2A9FCB'}}
                            >
                              <FileText className="w-4 h-4" /> Cartella
                            </button>
                            <button
                              onClick={() => updateAppointmentStatus(apt.id, 'completed')}
                              className="flex-1 px-3 py-1 rounded text-white text-sm flex items-center justify-center gap-1"
                              style={{background: STATUS_CONFIG.completed.color}}
                            >
                              <Check className="w-4 h-4" /> Completa
                            </button>
                          </div>
                        </div>
                      ))}
                      {(!salaAttesaData.by_status?.in_progress || salaAttesaData.by_status.in_progress.length === 0) && (
                        <p className="text-center text-[#2A9FCB]/60 text-sm py-4">Nessun paziente</p>
                      )}
                    </div>
                  </div>

                  {/* Completati */}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-4 border-b" style={{background: STATUS_CONFIG.completed.bg}}>
                      <h3 className="font-semibold flex items-center gap-2" style={{color: STATUS_CONFIG.completed.color}}>
                        {STATUS_CONFIG.completed.emoji} Completati ({salaAttesaData.by_status?.completed?.length || 0})
                      </h3>
                    </div>
                    <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
                      {salaAttesaData.by_status?.completed?.map(apt => (
                        <div key={apt.id} className="p-3 rounded-lg border opacity-75" style={{borderColor: STATUS_CONFIG.completed.color}}>
                          <div className="flex justify-between items-start mb-1">
                            <p className="font-medium">{apt.patient?.first_name} {apt.patient?.last_name}</p>
                            {apt.room && (
                              <span className="text-xs px-2 py-0.5 rounded-full " style={{background: ROOM_COLORS[apt.room]?.bg || '#F3F4F6', color: ROOM_COLORS[apt.room]?.text || '#4B5563'}}>
                                {apt.room}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#2A9FCB]/70">Completata: {apt.visit_completed_at ? new Date(apt.visit_completed_at).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'}) : '-'}</p>
                          {apt.doctor && <p className="text-xs text-[#2A9FCB]/60">Dott. {apt.doctor.last_name}</p>}
                          <p className="text-xs text-green-600 mt-1">✉️ Referto inviato</p>
                        </div>
                      ))}
                      {(!salaAttesaData.by_status?.completed || salaAttesaData.by_status.completed.length === 0) && (
                        <p className="text-center text-[#2A9FCB]/60 text-sm py-4">Nessun paziente</p>
                      )}
                    </div>
                  </div>

                  {/* Riepilogo */}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-4 border-b" style={{background: '#F0FAF5'}}>
                      <h3 className="font-semibold flex items-center gap-2" style={{color: '#2A9FCB'}}>
                        📊 Riepilogo Giornata
                      </h3>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-[#2A9FCB]/80">Totale appuntamenti:</span>
                        <span className="font-bold text-lg">{salaAttesaData.total || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[#2A9FCB]/80">Da visitare:</span>
                        <span className="font-bold" style={{color: STATUS_CONFIG.scheduled.color}}>
                          {(salaAttesaData.by_status?.scheduled?.length || 0) + (salaAttesaData.by_status?.checked_in?.length || 0) + (salaAttesaData.by_status?.paid?.length || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[#2A9FCB]/80">In corso:</span>
                        <span className="font-bold" style={{color: STATUS_CONFIG.in_progress.color}}>
                          {salaAttesaData.by_status?.in_progress?.length || 0}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[#2A9FCB]/80">Completate:</span>
                        <span className="font-bold" style={{color: STATUS_CONFIG.completed.color}}>
                          {salaAttesaData.by_status?.completed?.length || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Vista per Stanza */}
              {!loadingSalaAttesa && salaAttesaData && viewMode === 'room' && (
                <div className="space-y-6">
                  {Object.entries(salaAttesaData.by_room || {}).map(([roomName, roomApts]) => {
                    const roomConfig = ROOM_COLORS[roomName] || { bg: '#F3F4F6', text: '#4B5563', border: '#D1D5DB' };
                    return (
                      <div key={roomName} className="bg-white rounded-xl shadow-sm overflow-hidden">
                        <div 
                          className="p-4 border-b flex items-center justify-between"
                          style={{background: roomConfig.bg, borderColor: roomConfig.border}}
                        >
                          <h3 className="font-semibold flex items-center gap-2" style={{color: roomConfig.text}}>
                            <Building2 className="w-5 h-5" />
                            {roomName} ({roomApts.length} pazienti)
                          </h3>
                          <div className="flex gap-2">
                            {Object.entries(STATUS_CONFIG).slice(0, 4).map(([status, config]) => {
                              const count = roomApts.filter(a => a.status === status).length;
                              if (count === 0) return null;
                              return (
                                <span key={status} className="px-2 py-1 rounded-full  text-xs font-medium" style={{background: config.bg, color: config.color}}>
                                  {config.emoji} {count}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                            {roomApts.map(apt => {
                              const statusConfig = STATUS_CONFIG[apt.status] || STATUS_CONFIG.scheduled;
                              return (
                                <div 
                                  key={apt.id} 
                                  className="p-3 rounded-lg border-2 transition-all hover:shadow-md"
                                  style={{borderColor: statusConfig.color, background: statusConfig.bg}}
                                >
                                  <div className="flex items-start justify-between mb-2">
                                    <span className="text-xs font-medium px-2 py-0.5 rounded-full " style={{background: statusConfig.color, color: 'white'}}>
                                      {statusConfig.label}
                                    </span>
                                    <span className="text-xs text-[#2A9FCB]/70">
                                      {apt.appointment_date ? new Date(apt.appointment_date).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'}) : ''}
                                    </span>
                                  </div>
                                  <p className="font-medium text-[#2A9FCB]">{apt.patient?.first_name} {apt.patient?.last_name}</p>
                                  <p className="text-xs text-[#2A9FCB]/70 mb-2">{apt.reason}</p>
                                  {apt.doctor && (
                                    <p className="text-xs text-[#2A9FCB]/60">Dott. {apt.doctor.last_name}</p>
                                  )}
                                  
                                  {/* Action buttons based on status */}
                                  <div className="mt-2 flex gap-1">
                                    {apt.status === 'checked_in' && (
                                      <button
                                        onClick={() => updateAppointmentStatus(apt.id, 'paid')}
                                        className="flex-1 px-2 py-1 rounded text-white text-xs"
                                        style={{background: STATUS_CONFIG.paid.color}}
                                      >
                                        Pagato
                                      </button>
                                    )}
                                    {apt.status === 'paid' && (
                                      <button
                                        onClick={() => updateAppointmentStatus(apt.id, 'in_progress')}
                                        className="flex-1 px-2 py-1 rounded text-white text-xs"
                                        style={{background: STATUS_CONFIG.in_progress.color}}
                                      >
                                        Inizia
                                      </button>
                                    )}
                                    {apt.status === 'in_progress' && (
                                      <>
                                        <button
                                          onClick={() => navigate(`/cartella-clinica/${apt.patient_id}`)}
                                          className="flex-1 px-2 py-1 rounded border text-xs"
                                          style={{borderColor: '#2A9FCB', color: '#2A9FCB'}}
                                        >
                                          Cartella
                                        </button>
                                        <button
                                          onClick={() => updateAppointmentStatus(apt.id, 'completed')}
                                          className="flex-1 px-2 py-1 rounded text-white text-xs"
                                          style={{background: STATUS_CONFIG.completed.color}}
                                        >
                                          Completa
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {roomApts.length === 0 && (
                            <p className="text-center text-[#2A9FCB]/60 py-4">Nessun paziente in questa stanza</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  
                  {Object.keys(salaAttesaData.by_room || {}).length === 0 && (
                    <div className="bg-white rounded-xl p-8 text-center">
                      <Building2 className="w-12 h-12 mx-auto mb-4 text-[#2A9FCB]/50" />
                      <p className="text-[#2A9FCB]/70">Nessun appuntamento con stanza assegnata</p>
                    </div>
                  )}
                </div>
              )}

              {/* No data */}
              {!loadingSalaAttesa && !salaAttesaData && (
                <div className="bg-white rounded-xl p-12 text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[#2A9FCB]/50" />
                  <p className="text-[#2A9FCB]/70">Nessun dato disponibile per questa data</p>
                </div>
              )}
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold" style={{color: '#2A9FCB'}}>La Tua Esperienza Lavorativa</h2>
              <p className="text-[#2A9FCB]/70">
                Condividi la tua esperienza lavorativa con PRIME VISION. Le recensioni positive appariranno nella sezione &quot;Lavora con Noi&quot;!
              </p>
              <div className="max-w-lg">
                <ReviewForm userRole={user?.role} />
              </div>
            </div>
          )}

          {/* Turni e Disponibilità Tab */}
          {activeTab === 'turni' && (
            <ShiftsManager isAdmin={false} currentUserId={user?.id} />
          )}

          {/* Videoconsulti Tab */}
          {activeTab === 'videoconsulti' && (
            <VideoConsultManager isDoctor={true} currentUser={user} />
          )}

          {/* Template Clinici Tab */}
          {activeTab === 'templates' && (
            <ClinicalTemplates />
          )}

          {/* Profilo e Sicurezza Tab */}
          {activeTab === 'profilo' && (
            <div className="space-y-6">
              <div className="rounded-2xl p-6 shadow-sm" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
                <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2" style={{color: '#2A9FCB'}}>
                  <UserCircle className="w-6 h-6" />
                  Profilo e Sicurezza
                </h2>
                <p className="text-[#2A9FCB]/70 mb-6">
                  Gestisci il tuo profilo professionale e le impostazioni di sicurezza.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl p-6 border border-[#2A9FCB]/20">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{color: '#2A9FCB'}}>
                      <UserCircle className="w-5 h-5" />
                      Informazioni Profilo
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-[#2A9FCB]/70">Nome</label>
                        <p className="text-[#2A9FCB] font-medium">{user?.first_name} {user?.last_name}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-[#2A9FCB]/70">Email</label>
                        <p className="text-[#2A9FCB] font-medium">{user?.email}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-[#2A9FCB]/70">Ruolo</label>
                        <p className="text-[#2A9FCB] font-medium capitalize">{user?.role}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-[#2A9FCB]/70">Qualifica</label>
                        {editingProfile ? (
                          <input
                            type="text"
                            value={profileForm.specialization}
                            onChange={(e) => setProfileForm(prev => ({ ...prev, specialization: e.target.value }))}
                            className="w-full p-2 border border-[#2A9FCB]/30 rounded-lg mt-1"
                            placeholder="Es: Medico Oculista"
                            data-testid="specialization-input"
                          />
                        ) : (
                          <p className="text-[#2A9FCB] font-medium">{user?.specialization || 'Non specificata'}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Sedi di Lavoro */}
                  <div className="bg-white rounded-xl p-6 border border-[#2A9FCB]/20 md:col-span-2">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{color: '#2A9FCB'}}>
                      <MapPin className="w-5 h-5" />
                      Sedi di Lavoro
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">Seleziona le sedi dove sei disponibile a visitare</p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={async () => {
                          const current = user?.work_locations || [];
                          const updated = current.includes('milano') 
                            ? current.filter(l => l !== 'milano') 
                            : [...current, 'milano'];
                          try {
                            const token = localStorage.getItem('token');
                            await axios.put(`${API}/doctor/work-locations`, { work_locations: updated }, { headers: { Authorization: `Bearer ${token}` }});
                            setUser(prev => ({...prev, work_locations: updated}));
                            toast.success('Sedi aggiornate');
                          } catch (err) { toast.error('Errore aggiornamento sedi'); }
                        }}
                        className={`px-6 py-3 rounded-xl border-2 font-medium flex items-center gap-2 transition-all ${
                          (user?.work_locations || []).includes('milano')
                            ? 'border-orange-500 bg-orange-50 text-orange-700'
                            : 'border-gray-200 text-gray-500 hover:border-gray-300'
                        }`}
                        data-testid="work-location-milano"
                      >
                        <MapPin className="w-4 h-4" />
                        Milano — Via Santa Sofia 10
                        {(user?.work_locations || []).includes('milano') && <CheckCircle2 className="w-4 h-4 text-orange-500" />}
                      </button>
                      <button
                        onClick={async () => {
                          const current = user?.work_locations || [];
                          const updated = current.includes('napoli') 
                            ? current.filter(l => l !== 'napoli') 
                            : [...current, 'napoli'];
                          try {
                            const token = localStorage.getItem('token');
                            await axios.put(`${API}/doctor/work-locations`, { work_locations: updated }, { headers: { Authorization: `Bearer ${token}` }});
                            setUser(prev => ({...prev, work_locations: updated}));
                            toast.success('Sedi aggiornate');
                          } catch (err) { toast.error('Errore aggiornamento sedi'); }
                        }}
                        className={`px-6 py-3 rounded-xl border-2 font-medium flex items-center gap-2 transition-all ${
                          (user?.work_locations || []).includes('napoli')
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-500 hover:border-gray-300'
                        }`}
                        data-testid="work-location-napoli"
                      >
                        <MapPin className="w-4 h-4" />
                        Napoli — Via Posillipo 160
                        {(user?.work_locations || []).includes('napoli') && <CheckCircle2 className="w-4 h-4 text-blue-500" />}
                      </button>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-[#2A9FCB]/20">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2" style={{color: '#2A9FCB'}}>
                        <Shield className="w-5 h-5" />
                        Dati Professionali e Firma Digitale
                      </h3>
                      {!editingProfile ? (
                        <button
                          onClick={() => setEditingProfile(true)}
                          className="text-sm text-[#2A9FCB] hover:underline flex items-center gap-1"
                          data-testid="edit-profile-btn"
                        >
                          <Edit3 className="w-4 h-4" /> Modifica
                        </button>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingProfile(false);
                              setProfileForm({
                                numero_ordine: user?.numero_ordine || '',
                                specialization: user?.specialization || ''
                              });
                            }}
                            className="text-sm text-gray-500 hover:underline"
                          >
                            Annulla
                          </button>
                          <button
                            onClick={saveDoctorProfile}
                            disabled={savingProfile}
                            className="text-sm bg-[#2A9FCB] text-white px-3 py-1 rounded-lg hover:bg-[#1a8bb0] flex items-center gap-1"
                            data-testid="save-profile-btn"
                          >
                            {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            Salva
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-4">
                      <div className="p-4 bg-[#2A9FCB]/5 rounded-lg border border-[#2A9FCB]/20">
                        <label className="text-sm font-medium text-[#2A9FCB]/70">Numero Iscrizione Ordine dei Medici</label>
                        {editingProfile ? (
                          <input
                            type="text"
                            value={profileForm.numero_ordine}
                            onChange={(e) => setProfileForm(prev => ({ ...prev, numero_ordine: e.target.value }))}
                            className="w-full p-2 border border-[#2A9FCB]/30 rounded-lg mt-1 text-lg font-semibold"
                            placeholder="Es: NA038758"
                            data-testid="numero-ordine-input"
                          />
                        ) : (
                          <p className="text-[#2A9FCB] font-semibold text-lg mt-1">{user?.numero_ordine || 'Non inserito'}</p>
                        )}
                        <p className="text-xs text-[#2A9FCB]/60 mt-1">Utilizzato per la firma digitale dei referti</p>
                      </div>
                      {!editingProfile && (
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                            <Check className="w-4 h-4" /> Anteprima Firma Digitale
                          </h4>
                          <div className="text-sm text-green-700 space-y-1 border-t border-green-200 pt-2 mt-2">
                            <p className="font-semibold">{user?.first_name} {user?.last_name}</p>
                            <p>{user?.specialization || 'Medico'}</p>
                            {user?.numero_ordine && <p>N° Iscrizione Ordine: {user.numero_ordine}</p>}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Register Patient Modal */}
      {showRegisterPatientModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="register-patient-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
            <div className="p-5 bg-gradient-to-r from-[#daeef9] to-[#f0f7fc] border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold" style={{color: '#2A9FCB'}}>
                  <UserPlus className="w-5 h-5 inline mr-2" />Registra Nuovo Paziente
                </h3>
                <button onClick={() => setShowRegisterPatientModal(false)} className="p-1.5 hover:bg-white/50 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Nome *</label>
                  <input type="text" value={registerPatientData.first_name}
                    onChange={e => setRegisterPatientData(p => ({...p, first_name: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]/30"
                    data-testid="reg-first-name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Cognome *</label>
                  <input type="text" value={registerPatientData.last_name}
                    onChange={e => setRegisterPatientData(p => ({...p, last_name: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]/30"
                    data-testid="reg-last-name" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Email *</label>
                <input type="email" value={registerPatientData.email}
                  onChange={e => setRegisterPatientData(p => ({...p, email: e.target.value}))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]/30"
                  data-testid="reg-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Telefono *</label>
                  <input type="tel" value={registerPatientData.phone}
                    onChange={e => setRegisterPatientData(p => ({...p, phone: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]/30"
                    data-testid="reg-phone" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Data di Nascita *</label>
                  <input type="date" value={registerPatientData.date_of_birth}
                    onChange={e => setRegisterPatientData(p => ({...p, date_of_birth: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]/30"
                    data-testid="reg-dob" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Sesso</label>
                  <select value={registerPatientData.gender}
                    onChange={e => setRegisterPatientData(p => ({...p, gender: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-white">
                    <option value="">Seleziona</option>
                    <option value="M">Maschio</option>
                    <option value="F">Femmina</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Codice Fiscale</label>
                  <input type="text" value={registerPatientData.fiscal_code}
                    onChange={e => setRegisterPatientData(p => ({...p, fiscal_code: e.target.value.toUpperCase()}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]/30"
                    data-testid="reg-fiscal-code" maxLength={16} />
                </div>
              </div>
              <p className="text-xs text-gray-400">Password default: <strong>primevision</strong> (il paziente dovrà cambiarla al primo accesso)</p>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button onClick={() => setShowRegisterPatientModal(false)}
                className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">
                Annulla
              </button>
              <button onClick={handleRegisterPatient} disabled={registerPatientLoading}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:shadow-md disabled:opacity-50"
                style={{background: '#2A9FCB'}}
                data-testid="confirm-register-patient">
                {registerPatientLoading ? 'Registrazione...' : 'Registra Paziente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Availability Change Confirmation Modal */}
      {availabilityModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="availability-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              {availabilityModal.newValue ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-[#2A9FCB] mb-2">Attivare Disponibilità?</h3>
                  <p className="text-[#2A9FCB]/80">
                    Stai per <span className="font-semibold text-green-600">attivare</span> lo slot del{' '}
                    <span className="font-semibold">{new Date(availabilityModal.date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                    {' '}({availabilityModal.periodLabel}).
                  </p>
                  <div className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${availabilityLocation === 'milano' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                    <MapPin className="w-4 h-4" />
                    Sede: {availabilityLocation === 'milano' ? 'Milano — Via Santa Sofia 10' : 'Napoli — Via Posillipo 160'}
                  </div>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                    <Bell className="w-4 h-4 inline mr-2" />
                    L&apos;amministratore verrà notificato della nuova disponibilità.
                  </div>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold text-[#2A9FCB] mb-2">Disattivare Disponibilità?</h3>
                  <p className="text-[#2A9FCB]/80">
                    Stai per <span className="font-semibold text-orange-600">disattivare</span> lo slot del{' '}
                    <span className="font-semibold">{new Date(availabilityModal.date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                    {' '}({availabilityModal.periodLabel}).
                  </p>
                  <div className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${availabilityLocation === 'milano' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                    <MapPin className="w-4 h-4" />
                    Sede: {availabilityLocation === 'milano' ? 'Milano — Via Santa Sofia 10' : 'Napoli — Via Posillipo 160'}
                  </div>
                  
                  {availabilityModal.affectedAppointments.length > 0 ? (
                    <div className="mt-4 p-4 bg-red-50 rounded-lg text-left">
                      <p className="text-sm font-semibold text-red-700 mb-2">
                        ⚠️ {availabilityModal.affectedAppointments.length} paziente/i hanno appuntamenti prenotati:
                      </p>
                      <ul className="text-sm text-red-600 space-y-1 mb-3">
                        {availabilityModal.affectedAppointments.slice(0, 3).map((apt, i) => (
                          <li key={i}>• {apt.patient_first_name} {apt.patient_last_name} - {new Date(apt.appointment_date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</li>
                        ))}
                        {availabilityModal.affectedAppointments.length > 3 && (
                          <li className="text-[#2A9FCB]/70">...e altri {availabilityModal.affectedAppointments.length - 3}</li>
                        )}
                      </ul>
                      <div className="text-sm text-[#2A9FCB] border-t border-red-200 pt-3">
                        <p className="font-medium mb-1">Verranno notificati:</p>
                        <ul className="text-xs space-y-1 text-[#2A9FCB]/80">
                          <li>✓ <strong>Admin</strong> della clinica</li>
                          <li>✓ <strong>Altri medici</strong> disponibili in quella data</li>
                          <li>✓ <strong>Pazienti</strong> interessati con possibilità di:
                            <br/>- Essere visitati da un altro medico
                            <br/>- Riprogrammare con lo stesso medico
                          </li>
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-[#2A9FCB]/80">
                      Nessun appuntamento prenotato in questo slot.
                    </div>
                  )}
                </>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setAvailabilityModal({ show: false, date: null, period: null, currentValue: false, newValue: false, affectedAppointments: [] })}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-[#2A9FCB]/80 font-medium hover:bg-gray-50 transition-colors"
                data-testid="availability-cancel-btn"
              >
                Annulla
              </button>
              <button
                onClick={confirmAvailabilityChange}
                disabled={saving}
                className={`flex-1 px-4 py-3 rounded-xl text-white font-medium transition-colors disabled:opacity-50 ${
                  availabilityModal.newValue 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-orange-600 hover:bg-orange-700'
                }`}
                data-testid="availability-confirm-btn"
              >
                {saving ? 'Salvataggio...' : availabilityModal.newValue ? 'Sì, Attiva' : 'Sì, Disattiva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patient Profile Modal */}
      {patientProfileModal.show && patientProfileModal.patient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b sticky top-0 bg-white rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg" 
                       style={{background: patientProfileModal.patient.gender === 'F' ? '#EC4899' : '#2A9FCB'}}>
                    {patientProfileModal.patient.first_name?.[0]}{patientProfileModal.patient.last_name?.[0]}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#2A9FCB]">Modifica Profilo Paziente</h2>
                    <p className="text-sm text-gray-500">{patientProfileModal.patient.email}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setPatientProfileModal({ show: false, patient: null, loading: false, formData: {} })}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Form */}
            <div className="p-6 space-y-6">
              {/* Dati Anagrafici */}
              <div>
                <h3 className="font-semibold text-[#2A9FCB] mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Dati Anagrafici
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Nome *</label>
                    <input
                      type="text"
                      value={patientProfileModal.formData.first_name || ''}
                      onChange={(e) => updatePatientFormField('first_name', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Cognome *</label>
                    <input
                      type="text"
                      value={patientProfileModal.formData.last_name || ''}
                      onChange={(e) => updatePatientFormField('last_name', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Data di Nascita</label>
                    <input
                      type="date"
                      value={patientProfileModal.formData.date_of_birth || ''}
                      onChange={(e) => updatePatientFormField('date_of_birth', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Luogo di Nascita</label>
                    <input
                      type="text"
                      value={patientProfileModal.formData.place_of_birth || ''}
                      onChange={(e) => updatePatientFormField('place_of_birth', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Sesso</label>
                    <select
                      value={patientProfileModal.formData.gender || ''}
                      onChange={(e) => updatePatientFormField('gender', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                    >
                      <option value="">Seleziona...</option>
                      <option value="M">Uomo</option>
                      <option value="F">Donna</option>
                      <option value="X">Preferisco non dichiararlo</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Codice Fiscale</label>
                    <input
                      type="text"
                      value={patientProfileModal.formData.codice_fiscale || ''}
                      onChange={(e) => updatePatientFormField('codice_fiscale', e.target.value.toUpperCase())}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent uppercase"
                      maxLength={16}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Etnia/Razza</label>
                    <select
                      value={patientProfileModal.formData.ethnicity || ''}
                      onChange={(e) => updatePatientFormField('ethnicity', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                    >
                      <option value="">Seleziona...</option>
                      <option value="caucasica">Caucasica</option>
                      <option value="afro">Africana/Afroamericana</option>
                      <option value="asiatica">Asiatica</option>
                      <option value="ispanica">Ispanica/Latina</option>
                      <option value="mediorientale">Mediorientale</option>
                      <option value="nativo_americana">Nativo Americana</option>
                      <option value="pacifica">Oceanica/Pacifica</option>
                      <option value="mista">Mista</option>
                      <option value="altro">Altro</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Contatti */}
              <div>
                <h3 className="font-semibold text-[#2A9FCB] mb-3 flex items-center gap-2">
                  📞 Contatti
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Telefono *</label>
                    <input
                      type="tel"
                      value={patientProfileModal.formData.phone || ''}
                      onChange={(e) => updatePatientFormField('phone', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Email</label>
                    <input
                      type="email"
                      value={patientProfileModal.formData.email || ''}
                      className="w-full px-3 py-2 border rounded-lg bg-gray-100 cursor-not-allowed"
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">L&apos;email non può essere modificata</p>
                  </div>
                </div>
              </div>

              {/* Indirizzo */}
              <div>
                <h3 className="font-semibold text-[#2A9FCB] mb-3 flex items-center gap-2">
                  📍 Indirizzo di Residenza
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1 text-gray-700">Via/Piazza *</label>
                    <input
                      type="text"
                      value={patientProfileModal.formData.address_street || ''}
                      onChange={(e) => updatePatientFormField('address_street', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">N° *</label>
                    <input
                      type="text"
                      value={patientProfileModal.formData.address_number || ''}
                      onChange={(e) => updatePatientFormField('address_number', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">CAP *</label>
                    <input
                      type="text"
                      value={patientProfileModal.formData.address_cap || ''}
                      onChange={(e) => updatePatientFormField('address_cap', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                      maxLength={5}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1 text-gray-700">Città *</label>
                    <input
                      type="text"
                      value={patientProfileModal.formData.address_city || ''}
                      onChange={(e) => updatePatientFormField('address_city', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Info Mediche */}
              <div>
                <h3 className="font-semibold text-[#2A9FCB] mb-3 flex items-center gap-2">
                  🩸 Info Mediche
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Gruppo Sanguigno</label>
                    <select
                      value={patientProfileModal.formData.blood_type || ''}
                      onChange={(e) => updatePatientFormField('blood_type', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                    >
                      <option value="">Non specificato</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="0+">0+</option>
                      <option value="0-">0-</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t sticky bottom-0 bg-white rounded-b-2xl">
              <div className="flex gap-3">
                <button
                  onClick={() => setPatientProfileModal({ show: false, patient: null, loading: false, formData: {} })}
                  className="flex-1 px-4 py-3 rounded-lg border border-gray-200 text-gray-700 font-medium hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  onClick={() => navigate(`/cartella-clinica/${patientProfileModal.patient.id}`)}
                  className="px-4 py-3 rounded-lg border border-[#2A9FCB] text-[#2A9FCB] font-medium hover:bg-[#2A9FCB]/10 flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Cartella Clinica
                </button>
                <button
                  onClick={savePatientProfile}
                  disabled={patientProfileModal.loading}
                  className="flex-1 px-4 py-3 rounded-lg text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{background: '#2A9FCB'}}
                >
                  {patientProfileModal.loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      Salva Modifiche
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Day View Modal - Google Calendar Style */}
      {showDayView && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowDayView(false)}>
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b bg-gradient-to-r from-[#2A9FCB] to-[#2A9FCB]">
              <div className="flex items-center justify-between">
                <div className="text-white">
                  <h2 className="text-2xl font-bold">
                    {dayViewData?.day_name} {dayViewDate ? new Date(dayViewDate).getDate() : ''} {dayViewDate ? MONTHS[new Date(dayViewDate).getMonth()] : ''}
                  </h2>
                  <p className="text-white/80 text-sm mt-1">
                    {dayViewData?.total_count || 0} appuntamenti programmati
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                      availabilityLocation === 'milano' ? 'bg-orange-400/40' : 'bg-sky-400/40'
                    }`}>
                      {availabilityLocation === 'milano' ? 'Milano' : 'Napoli'}
                    </span>
                    {dayViewData?.availability?.morning_available && <span className="ml-2 px-2 py-0.5 bg-amber-400/30 rounded text-xs">Mattina</span>}
                    {dayViewData?.availability?.afternoon_available && <span className="ml-2 px-2 py-0.5 bg-indigo-400/30 rounded text-xs">Pomeriggio</span>}
                  </p>
                </div>
                <button
                  onClick={() => setShowDayView(false)}
                  className="p-2 rounded-full hover:bg-white/20 transition-all text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Timeline Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingDayView ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-[#2A9FCB]" />
                </div>
              ) : dayAppointments.length === 0 ? (
                <div className="text-center py-20">
                  <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 text-lg">Nessun appuntamento per questo giorno</p>
                  <p className="text-gray-400 text-sm mt-2">I pazienti possono prenotare nelle fasce orarie attive</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Time slots from 8:00 to 20:00 */}
                  {Array.from({ length: 13 }, (_, i) => i + 8).map(hour => {
                    const hourAppointments = dayAppointments.filter(apt => apt.hour === hour);
                    const isMorning = hour < 13;
                    const isAfternoon = hour >= 15;
                    const isInActiveSlot = (isMorning && dayViewData?.availability?.morning_available) || 
                                          (isAfternoon && dayViewData?.availability?.afternoon_available);
                    
                    return (
                      <div 
                        key={hour} 
                        className={`flex border-b border-gray-100 min-h-[70px] ${isInActiveSlot ? '' : 'bg-gray-50/50'}`}
                      >
                        {/* Time label */}
                        <div className="w-20 flex-shrink-0 py-3 pr-4 text-right">
                          <span className={`text-sm font-medium ${isInActiveSlot ? 'text-[#2A9FCB]' : 'text-gray-400'}`}>
                            {hour.toString().padStart(2, '0')}:00
                          </span>
                        </div>
                        
                        {/* Appointments */}
                        <div className="flex-1 py-2 pl-4 border-l border-gray-200">
                          {hourAppointments.length > 0 ? (
                            <div className="space-y-2">
                              {hourAppointments.map(apt => (
                                <div 
                                  key={apt.id}
                                  className="bg-gradient-to-r from-[#2A9FCB]/10 to-[#2A9FCB]/10 border-l-4 border-[#2A9FCB] rounded-r-lg p-3 hover:shadow-md transition-all cursor-pointer"
                                  onClick={() => apt.patient && openPatientProfile(apt.patient)}
                                  data-testid={`appointment-${apt.id}`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-bold text-[#2A9FCB]">{apt.time}</span>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                          apt.status === 'completed' ? 'bg-green-100 text-green-700' :
                                          apt.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                          apt.status === 'in_progress' ? 'bg-purple-100 text-purple-700' :
                                          'bg-blue-100 text-blue-700'
                                        }`}>
                                          {STATUS_CONFIG[apt.status]?.label || apt.status}
                                        </span>
                                        {apt.appointment_type === 'telemedicine' && (
                                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs flex items-center gap-1">
                                            <Video className="w-3 h-3" />
                                            Tele
                                          </span>
                                        )}
                                      </div>
                                      
                                      {apt.patient && (
                                        <div className="mt-2">
                                          <p className="font-semibold text-gray-800 flex items-center gap-2">
                                            <Users className="w-4 h-4 text-[#2A9FCB]" />
                                            {apt.patient.first_name} {apt.patient.last_name}
                                          </p>
                                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-600">
                                            {apt.patient.phone && (
                                              <span className="flex items-center gap-1">
                                                📱 {apt.patient.phone}
                                              </span>
                                            )}
                                            {apt.patient.email && (
                                              <span className="flex items-center gap-1 truncate max-w-[200px]">
                                                ✉️ {apt.patient.email}
                                              </span>
                                            )}
                                            {apt.patient.date_of_birth && (
                                              <span className="flex items-center gap-1">
                                                🎂 {new Date(apt.patient.date_of_birth).toLocaleDateString('it-IT')}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {apt.reason && (
                                        <p className="text-sm text-gray-500 mt-2 italic">
                                          📋 {apt.reason}
                                        </p>
                                      )}
                                    </div>
                                    
                                    {/* Actions */}
                                    <div className="flex gap-2 ml-4">
                                      {apt.patient && (
                                        <>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); navigate(`/cartella-clinica/${apt.patient.id}`); }}
                                            className="p-2 rounded-lg bg-[#2A9FCB]/10 hover:bg-[#2A9FCB]/20 text-[#2A9FCB] transition-all"
                                            title="Apri Cartella Clinica"
                                          >
                                            <FileText className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); navigate(`/cartella-clinica/${apt.patient.id}`); }}
                                            className="p-2 rounded-lg bg-green-100 hover:bg-green-200 text-green-700 transition-all"
                                            title="Inizia Visita"
                                          >
                                            <Play className="w-4 h-4" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : isInActiveSlot ? (
                            <div className="h-full flex items-center">
                              <span className="text-xs text-gray-400">Slot disponibile</span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-[#2A9FCB]"></div>
                  Clicca sul paziente per vedere il profilo
                </span>
              </div>
              <button
                onClick={() => setShowDayView(false)}
                className="px-4 py-2 rounded-lg bg-[#2A9FCB] text-white font-medium hover:bg-[#2A9FCB]/90 transition-all"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Google Calendar Sync Modal */}
      {calendarSyncModal.show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCalendarSyncModal({ show: false, loading: false, syncData: null })}>
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b bg-gradient-to-r from-[#2A9FCB] to-[#2A9FCB]">
              <div className="flex items-center justify-between">
                <div className="text-white">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Calendar className="w-6 h-6" />
                    Sincronizza con Google Calendar
                  </h2>
                  <p className="text-white/80 text-sm mt-1">
                    Collega il tuo calendario PRIME VISION
                  </p>
                </div>
                <button
                  onClick={() => setCalendarSyncModal({ show: false, loading: false, syncData: null })}
                  className="p-2 rounded-full hover:bg-white/20 transition-all text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {calendarSyncModal.loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#2A9FCB]" />
                  <span className="ml-3 text-gray-600">Preparazione sincronizzazione...</span>
                </div>
              ) : calendarSyncModal.syncData ? (
                <div className="space-y-6">
                  {/* Quick Sync Button */}
                  <div className="text-center">
                    <button
                      onClick={openGoogleCalendarSync}
                      className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3"
                    >
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.383 0 0 5.383 0 12s5.383 12 12 12 12-5.383 12-12S18.617 0 12 0zm-1.2 17.4h2.4v-2.4h-2.4v2.4zm0-4.8h2.4V6.6h-2.4v6z"/>
                      </svg>
                      Aggiungi a Google Calendar
                    </button>
                    <p className="text-sm text-gray-500 mt-2">
                      Si aprirà Google Calendar per confermare l&apos;aggiunta
                    </p>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-[#2A9FCB]" />
                      Il tuo calendario includerà:
                    </h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      {calendarSyncModal.syncData.user_role === 'patient' ? (
                        <>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Le tue visite e appuntamenti
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Promemoria automatici (24h e 2h prima)
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Dettagli medico e sede
                          </li>
                        </>
                      ) : (
                        <>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            I tuoi turni di lavoro (mattina/pomeriggio)
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Appuntamenti con i tuoi pazienti
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Nome paziente e motivo visita
                          </li>
                        </>
                      )}
                    </ul>
                  </div>

                  {/* Manual URL */}
                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-gray-700 mb-3">
                      Oppure copia l&apos;URL manualmente:
                    </h3>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={calendarSyncModal.syncData.feed_url}
                        className="flex-1 px-3 py-2 text-sm border rounded-lg bg-gray-50 text-gray-600 truncate"
                      />
                      <button
                        onClick={copyFeedUrl}
                        className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-all"
                      >
                        Copia
                      </button>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-sm text-amber-800">
                      <strong>💡 Nota:</strong> Google Calendar si aggiorna automaticamente ogni 12-24 ore. 
                      Per aggiornamenti immediati, puoi rimuovere e riaggiungere il calendario.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Internal Messages Modal */}
      <InternalMessages 
        isOpen={showMessages} 
        onClose={() => setShowMessages(false)} 
        currentUser={user}
      />
    </div>
  );
};

export default DoctorDashboard;
